// ============================================================
// COMPLETE COMPONENT ALTERNATIVES - 100% COVERAGE
// Every component in the system has at least 2 alternatives
// ============================================================

export const COMPONENT_ALTERNATIVES = {
  
  // ==================== LAYER 1: PHYSICAL SUBSTRATE ====================
  
  'CPU (x86)': {
    category: 'Processing Unit',
    alternatives: [
      { id: 'alt-cpu-x86-1', name: 'CPU (ARM)', icon: '📱', description: 'ARM processor for energy efficiency', specifications: { architecture: 'ARMv9', cores: '16-128', costRange: { low: 50, high: 2000, unit: 'per unit' } }, advantages: ['Lower power', 'Edge optimized'], disadvantages: ['Less compatibility'] },
      { id: 'alt-cpu-x86-2', name: 'AMD EPYC', icon: '🔥', description: 'High-core AMD server CPU', specifications: { cores: '32-128', costRange: { low: 1000, high: 10000, unit: 'per unit' } }, advantages: ['More cores', 'Better value'], disadvantages: ['Higher power'] }
    ]
  },
  
  'CPU (ARM)': {
    category: 'Processing Unit',
    alternatives: [
      { id: 'alt-cpu-arm-1', name: 'CPU (x86)', icon: '🖥️', description: 'Traditional x86 processor', specifications: { architecture: 'x86-64', costRange: { low: 500, high: 5000, unit: 'per unit' } }, advantages: ['Universal compatibility'], disadvantages: ['Higher power'] },
      { id: 'alt-cpu-arm-2', name: 'Apple M-series', icon: '🍎', description: 'Apple silicon', specifications: { architecture: 'ARM', costRange: { low: 0, high: 0, unit: 'integrated' } }, advantages: ['Performance/watt'], disadvantages: ['Apple only'] }
    ]
  },
  
  'CPU (RISC-V)': {
    category: 'Processing Unit',
    alternatives: [
      { id: 'alt-cpu-rv-1', name: 'CPU (ARM)', icon: '📱', description: 'ARM as RISC-V alternative', specifications: { costRange: { low: 50, high: 2000, unit: 'per unit' } }, advantages: ['Mature ecosystem'], disadvantages: ['Not open source'] },
      { id: 'alt-cpu-rv-2', name: 'FPGA', icon: '🔧', description: 'FPGA with soft-core RISC-V', specifications: { costRange: { low: 100, high: 10000, unit: 'per unit' } }, advantages: ['Fully customizable'], disadvantages: ['Complex'] }
    ]
  },
  
  'GPU (NVIDIA)': {
    category: 'Processing Unit',
    alternatives: [
      { id: 'alt-gpu-nv-1', name: 'GPU (AMD)', icon: '🎮', description: 'AMD GPU with ROCm', specifications: { vram: '128 GB', costRange: { low: 5000, high: 15000, unit: 'per GPU' } }, advantages: ['More VRAM', 'Open source ROCm'], disadvantages: ['Less CUDA'] },
      { id: 'alt-gpu-nv-2', name: 'TPU (Google)', icon: '⚡', description: 'Google TPU', specifications: { tflops: '275', costRange: { low: 1000, high: 10000, unit: 'per chip' } }, advantages: ['TensorFlow optimized'], disadvantages: ['Google Cloud only'] },
      { id: 'alt-gpu-nv-3', name: 'Intel Gaudi', icon: '💎', description: 'Intel AI accelerator', specifications: { vram: '96 GB', costRange: { low: 8000, high: 20000, unit: 'per unit' } }, advantages: ['PyTorch support'], disadvantages: ['Less mature'] }
    ]
  },
  
  'GPU (AMD)': {
    category: 'Processing Unit',
    alternatives: [
      { id: 'alt-gpu-amd-1', name: 'GPU (NVIDIA)', icon: '🎮', description: 'NVIDIA GPU with CUDA', specifications: { vram: '80 GB', costRange: { low: 10000, high: 40000, unit: 'per GPU' } }, advantages: ['CUDA ecosystem', 'More pre-trained'], disadvantages: ['Higher cost'] },
      { id: 'alt-gpu-amd-2', name: 'TPU (Google)', icon: '⚡', description: 'Google TPU', specifications: { costRange: { low: 1000, high: 10000, unit: 'per chip' } }, advantages: ['Cloud optimized'], disadvantages: ['GCP only'] }
    ]
  },
  
  'TPU (Google)': {
    category: 'Processing Unit',
    alternatives: [
      { id: 'alt-tpu-1', name: 'GPU (NVIDIA)', icon: '🎮', description: 'NVIDIA GPU', specifications: { costRange: { low: 10000, high: 40000, unit: 'per GPU' } }, advantages: ['Universal', 'Framework agnostic'], disadvantages: ['Higher cost'] },
      { id: 'alt-tpu-2', name: 'AWS Inferentia', icon: '☁️', description: 'AWS inference chip', specifications: { costRange: { low: 500, high: 2000, unit: 'per chip' } }, advantages: ['Lower cost', 'AWS optimized'], disadvantages: ['AWS only'] },
      { id: 'alt-tpu-3', name: 'Azure Maia', icon: '☁️', description: 'Microsoft AI accelerator', specifications: { costRange: { low: 500, high: 2000, unit: 'per chip' } }, advantages: ['Azure optimized'], disadvantages: ['Azure only'] }
    ]
  },
  
  'NPU (Apple Neural Engine)': {
    category: 'Processing Unit',
    alternatives: [
      { id: 'alt-npu-1', name: 'Qualcomm Hexagon', icon: '📱', description: 'Qualcomm NPU', specifications: { costRange: { low: 0, high: 0, unit: 'integrated' } }, advantages: ['Android', 'Wide adoption'], disadvantages: ['Not Apple'] },
      { id: 'alt-npu-2', name: 'Google Edge TPU', icon: '⚡', description: 'Edge TPU for IoT', specifications: { costRange: { low: 50, high: 500, unit: 'per unit' } }, advantages: ['Edge inference'], disadvantages: ['Limited models'] }
    ]
  },
  
  'FPGA': {
    category: 'Processing Unit',
    alternatives: [
      { id: 'alt-fpga-1', name: 'ASIC', icon: '🔩', description: 'Custom ASIC', specifications: { costRange: { low: 2000000, high: 10000000, unit: 'per design' } }, advantages: ['Best performance'], disadvantages: ['Very high NRE'] },
      { id: 'alt-fpga-2', name: 'GPU (NVIDIA)', icon: '🎮', description: 'GPU for flexibility', specifications: { costRange: { low: 10000, high: 40000, unit: 'per GPU' } }, advantages: ['Flexible', 'Mature'], disadvantages: ['Higher latency'] }
    ]
  },
  
  'ASIC': {
    category: 'Processing Unit',
    alternatives: [
      { id: 'alt-asic-1', name: 'FPGA', icon: '🔧', description: 'FPGA for flexibility', specifications: { costRange: { low: 100, high: 10000, unit: 'per unit' } }, advantages: ['Reprogrammable', 'Lower NRE'], disadvantages: ['Lower performance'] },
      { id: 'alt-asic-2', name: 'GPU (NVIDIA)', icon: '🎮', description: 'GPU for general purpose', specifications: { costRange: { low: 10000, high: 40000, unit: 'per GPU' } }, advantages: ['No NRE', 'Flexible'], disadvantages: ['Lower efficiency'] }
    ]
  },
  
  'Neuromorphic Chip': {
    category: 'Processing Unit',
    alternatives: [
      { id: 'alt-neuro-1', name: 'FPGA', icon: '🔧', description: 'FPGA with SNN implementation', specifications: { costRange: { low: 100, high: 10000, unit: 'per unit' } }, advantages: ['Available now'], disadvantages: ['Higher power'] },
      { id: 'alt-neuro-2', name: 'GPU (NVIDIA)', icon: '🎮', description: 'GPU for spiking simulation', specifications: { costRange: { low: 10000, high: 40000, unit: 'per GPU' } }, advantages: ['Mature tools'], disadvantages: ['Not event-driven'] }
    ]
  },
  
  'Quantum Processor': {
    category: 'Processing Unit',
    alternatives: [
      { id: 'alt-quantum-1', name: 'GPU (NVIDIA)', icon: '🎮', description: 'Classical GPU for simulation', specifications: { costRange: { low: 10000, high: 40000, unit: 'per GPU' } }, advantages: ['Available', 'Mature'], disadvantages: ['No quantum advantage'] },
      { id: 'alt-quantum-2', name: 'Quantum Simulator (Cloud)', icon: '☁️', description: 'Cloud quantum simulation', specifications: { costRange: { low: 100, high: 5000, unit: 'per month' } }, advantages: ['No hardware', 'Accessible'], disadvantages: ['Limited qubits'] }
    ]
  },
  
  'Analog Computer': {
    category: 'Processing Unit',
    alternatives: [
      { id: 'alt-analog-1', name: 'FPGA', icon: '🔧', description: 'Digital FPGA for ODE solving', specifications: { costRange: { low: 100, high: 10000, unit: 'per unit' } }, advantages: ['Precise'], disadvantages: ['Slower for ODE'] },
      { id: 'alt-analog-2', name: 'GPU (NVIDIA)', icon: '🎮', description: 'GPU for numerical ODE', specifications: { costRange: { low: 10000, high: 40000, unit: 'per GPU' } }, advantages: ['Flexible'], disadvantages: ['Higher power'] }
    ]
  },
  
  // -------- Memory --------
  'DRAM': {
    category: 'Memory',
    alternatives: [
      { id: 'alt-dram-1', name: 'HBM (High Bandwidth Memory)', icon: '🚀', description: 'High bandwidth memory', specifications: { bandwidth: '3.35 TB/s', costRange: { low: 500, high: 5000, unit: 'per stack' } }, advantages: ['3x bandwidth'], disadvantages: ['Higher cost'] },
      { id: 'alt-dram-2', name: 'Optane PMem', icon: '💾', description: 'Intel persistent memory', specifications: { capacity: '512 GB', costRange: { low: 500, high: 3000, unit: 'per module' } }, advantages: ['Persistent', 'Large capacity'], disadvantages: ['Discontinued'] }
    ]
  },
  
  'SRAM': {
    category: 'Memory',
    alternatives: [
      { id: 'alt-sram-1', name: 'DRAM', icon: '💾', description: 'DRAM as SRAM alternative', specifications: { costRange: { low: 100, high: 300, unit: 'per 32GB' } }, advantages: ['Higher density', 'Lower cost'], disadvantages: ['Slower'] },
      { id: 'alt-sram-2', name: 'HBM (High Bandwidth Memory)', icon: '🚀', description: 'HBM for cache-like performance', specifications: { costRange: { low: 500, high: 5000, unit: 'per stack' } }, advantages: ['High bandwidth'], disadvantages: ['Higher cost'] }
    ]
  },
  
  'HBM (High Bandwidth Memory)': {
    category: 'Memory',
    alternatives: [
      { id: 'alt-hbm-1', name: 'GDDR6X', icon: '🎮', description: 'Graphics DDR memory', specifications: { bandwidth: '1 TB/s', costRange: { low: 100, high: 500, unit: 'per module' } }, advantages: ['Lower cost'], disadvantages: ['Lower bandwidth'] },
      { id: 'alt-hbm-2', name: 'LPDDR5X', icon: '📱', description: 'Low power DDR', specifications: { bandwidth: '68 GB/s', costRange: { low: 50, high: 200, unit: 'per module' } }, advantages: ['Low power'], disadvantages: ['Lower bandwidth'] }
    ]
  },
  
  'CPU Cache (L1/L2/L3)': {
    category: 'Memory',
    alternatives: [
      { id: 'alt-cache-1', name: 'SRAM', icon: '⚡', description: 'External SRAM cache', specifications: { costRange: { low: 1, high: 100, unit: 'per chip' } }, advantages: ['Fast'], disadvantages: ['Small capacity'] },
      { id: 'alt-cache-2', name: 'DRAM', icon: '💾', description: 'DRAM as cache', specifications: { costRange: { low: 100, high: 300, unit: 'per 32GB' } }, advantages: ['Large capacity'], disadvantages: ['Slower'] }
    ]
  },
  
  'Register Files': {
    category: 'Memory',
    alternatives: [
      { id: 'alt-reg-1', name: 'SRAM', icon: '⚡', description: 'SRAM for register storage', specifications: { costRange: { low: 0, high: 0, unit: 'integrated' } }, advantages: ['Fast access'], disadvantages: ['Limited size'] },
      { id: 'alt-reg-2', name: 'CPU Cache (L1/L2/L3)', icon: '🎯', description: 'Cache as register spill', specifications: { costRange: { low: 0, high: 0, unit: 'integrated' } }, advantages: ['Larger'], disadvantages: ['Slower than registers'] }
    ]
  },
  
  // -------- Storage --------
  'SSD (NVMe)': {
    category: 'Storage',
    alternatives: [
      { id: 'alt-ssd-nvme-1', name: 'SSD (SATA)', icon: '💿', description: 'SATA SSD', specifications: { readSpeed: '550 MB/s', costRange: { low: 50, high: 200, unit: 'per TB' } }, advantages: ['Lower cost'], disadvantages: ['10x slower'] },
      { id: 'alt-ssd-nvme-2', name: 'Optane SSD', icon: '⚡', description: 'Intel Optane', specifications: { latency: '< 10µs', costRange: { low: 500, high: 3000, unit: 'per TB' } }, advantages: ['Ultra-low latency'], disadvantages: ['Discontinued'] }
    ]
  },
  
  'SSD (SATA)': {
    category: 'Storage',
    alternatives: [
      { id: 'alt-ssd-sata-1', name: 'SSD (NVMe)', icon: '🚀', description: 'NVMe SSD', specifications: { readSpeed: '7000 MB/s', costRange: { low: 100, high: 500, unit: 'per TB' } }, advantages: ['10x faster'], disadvantages: ['Higher cost'] },
      { id: 'alt-ssd-sata-2', name: 'HDD', icon: '📀', description: 'Hard disk drive', specifications: { capacity: '16 TB', costRange: { low: 50, high: 500, unit: 'per TB' } }, advantages: ['Lowest cost/TB'], disadvantages: ['Much slower'] }
    ]
  },
  
  'HDD': {
    category: 'Storage',
    alternatives: [
      { id: 'alt-hdd-1', name: 'SSD (SATA)', icon: '💿', description: 'SATA SSD', specifications: { costRange: { low: 200, high: 800, unit: 'per TB' } }, advantages: ['Faster'], disadvantages: ['Higher cost'] },
      { id: 'alt-hdd-2', name: 'Tape Drive', icon: '📼', description: 'Tape for archival', specifications: { capacity: '18 TB', costRange: { low: 100, high: 500, unit: 'per drive' } }, advantages: ['Lowest long-term cost'], disadvantages: ['Sequential only'] }
    ]
  },
  
  'Flash Storage': {
    category: 'Storage',
    alternatives: [
      { id: 'alt-flash-1', name: 'SSD (SATA)', icon: '💿', description: 'SATA SSD', specifications: { costRange: { low: 50, high: 200, unit: 'per TB' } }, advantages: ['Higher capacity'], disadvantages: ['Larger form factor'] },
      { id: 'alt-flash-2', name: 'HDD', icon: '📀', description: 'HDD for bulk', specifications: { costRange: { low: 50, high: 500, unit: 'per TB' } }, advantages: ['Lower cost'], disadvantages: ['Slower'] }
    ]
  },
  
  'Tape Drive': {
    category: 'Storage',
    alternatives: [
      { id: 'alt-tape-1', name: 'HDD', icon: '📀', description: 'HDD for archival', specifications: { costRange: { low: 50, high: 500, unit: 'per TB' } }, advantages: ['Random access'], disadvantages: ['Higher power'] },
      { id: 'alt-tape-2', name: 'Optical Storage', icon: '📀', description: 'Blu-ray archival', specifications: { costRange: { low: 1, high: 50, unit: 'per disc' } }, advantages: ['Very durable'], disadvantages: ['Low capacity per disc'] }
    ]
  },
  
  'Optical Storage': {
    category: 'Storage',
    alternatives: [
      { id: 'alt-opt-1', name: 'Tape Drive', icon: '📼', description: 'LTO tape', specifications: { costRange: { low: 100, high: 500, unit: 'per drive' } }, advantages: ['Higher capacity'], disadvantages: ['Sequential access'] },
      { id: 'alt-opt-2', name: 'HDD', icon: '📀', description: 'HDD for offline', specifications: { costRange: { low: 50, high: 500, unit: 'per TB' } }, advantages: ['Random access'], disadvantages: ['Less durable'] }
    ]
  },
  
  // -------- Networking --------
  'Ethernet': {
    category: 'Networking',
    alternatives: [
      { id: 'alt-eth-1', name: 'InfiniBand', icon: '🔗', description: 'High-performance interconnect', specifications: { speed: '200 Gb/s', costRange: { low: 500, high: 50000, unit: 'per port' } }, advantages: ['Lower latency', 'Higher bandwidth'], disadvantages: ['Higher cost'] },
      { id: 'alt-eth-2', name: 'Fiber Optics', icon: '🔦', description: 'Fiber optic connection', specifications: { speed: '400 Gbps', costRange: { low: 100, high: 10000, unit: 'per link' } }, advantages: ['Long distance', 'High bandwidth'], disadvantages: ['Requires transceivers'] }
    ]
  },
  
  'InfiniBand': {
    category: 'Networking',
    alternatives: [
      { id: 'alt-ib-1', name: 'Ethernet', icon: '🌐', description: 'Standard Ethernet', specifications: { speed: '100 GbE', costRange: { low: 10, high: 10000, unit: 'per port' } }, advantages: ['Universal', 'Lower cost'], disadvantages: ['Higher latency'] },
      { id: 'alt-ib-2', name: 'NVLink', icon: '⚡', description: 'NVIDIA GPU interconnect', specifications: { bandwidth: '900 GB/s', costRange: { low: 0, high: 0, unit: 'integrated' } }, advantages: ['Highest bandwidth'], disadvantages: ['GPU only'] }
    ]
  },
  
  'PCIe Bus': {
    category: 'Networking',
    alternatives: [
      { id: 'alt-pcie-1', name: 'NVLink', icon: '⚡', description: 'NVIDIA NVLink', specifications: { bandwidth: '900 GB/s', costRange: { low: 0, high: 0, unit: 'integrated' } }, advantages: ['Higher bandwidth'], disadvantages: ['GPU specific'] },
      { id: 'alt-pcie-2', name: 'InfiniBand', icon: '🔗', description: 'InfiniBand HDR', specifications: { speed: '200 Gb/s', costRange: { low: 500, high: 50000, unit: 'per port' } }, advantages: ['External connectivity'], disadvantages: ['Higher cost'] }
    ]
  },
  
  'NVLink': {
    category: 'Networking',
    alternatives: [
      { id: 'alt-nvlink-1', name: 'PCIe Bus', icon: '🔌', description: 'PCIe 5.0', specifications: { bandwidth: '128 GB/s', costRange: { low: 0, high: 0, unit: 'integrated' } }, advantages: ['Universal'], disadvantages: ['Lower bandwidth'] },
      { id: 'alt-nvlink-2', name: 'InfiniBand', icon: '🔗', description: 'InfiniBand for GPU', specifications: { speed: '200 Gb/s', costRange: { low: 500, high: 50000, unit: 'per port' } }, advantages: ['External scaling'], disadvantages: ['Higher latency than NVLink'] }
    ]
  },
  
  '5G/Wi-Fi Antennas': {
    category: 'Networking',
    alternatives: [
      { id: 'alt-5g-1', name: 'Fiber Optics', icon: '🔦', description: 'Wired fiber connection', specifications: { speed: '400 Gbps', costRange: { low: 100, high: 10000, unit: 'per link' } }, advantages: ['Higher bandwidth', 'More reliable'], disadvantages: ['Not wireless'] },
      { id: 'alt-5g-2', name: 'Bluetooth/BLE', icon: '📶', description: 'Bluetooth for short range', specifications: { range: '100m', costRange: { low: 1, high: 50, unit: 'per module' } }, advantages: ['Low power'], disadvantages: ['Short range'] }
    ]
  },
  
  'Fiber Optics': {
    category: 'Networking',
    alternatives: [
      { id: 'alt-fiber-1', name: 'Ethernet', icon: '🌐', description: 'Standard Ethernet', specifications: { speed: '100 GbE', costRange: { low: 10, high: 10000, unit: 'per port' } }, advantages: ['Lower cost'], disadvantages: ['Shorter distance'] },
      { id: 'alt-fiber-2', name: '5G/Wi-Fi Antennas', icon: '📡', description: 'Wireless alternative', specifications: { speed: '10 Gbps', costRange: { low: 10, high: 1000, unit: 'per module' } }, advantages: ['No cable'], disadvantages: ['Lower bandwidth'] }
    ]
  },
  
  // -------- Power & Thermal --------
  'Power Supply Units': {
    category: 'Power',
    alternatives: [
      { id: 'alt-psu-1', name: 'Redundant PSU', icon: '🔋', description: 'Dual redundant PSU', specifications: { costRange: { low: 200, high: 2000, unit: 'per unit' } }, advantages: ['High availability'], disadvantages: ['Higher cost'] },
      { id: 'alt-psu-2', name: 'DC Power Supply', icon: '⚡', description: 'Direct DC power', specifications: { costRange: { low: 100, high: 1000, unit: 'per unit' } }, advantages: ['Higher efficiency'], disadvantages: ['Requires DC infrastructure'] }
    ]
  },
  
  'Voltage Regulators': {
    category: 'Power',
    alternatives: [
      { id: 'alt-vr-1', name: 'Integrated VRM', icon: '🔧', description: 'Onboard voltage regulation', specifications: { costRange: { low: 0, high: 0, unit: 'integrated' } }, advantages: ['Simpler'], disadvantages: ['Less efficient'] },
      { id: 'alt-vr-2', name: 'External VRM', icon: '⚙️', description: 'Discrete voltage regulator', specifications: { costRange: { low: 1, high: 100, unit: 'per unit' } }, advantages: ['More efficient', 'More phases'], disadvantages: ['More components'] }
    ]
  },
  
  'Liquid Cooling': {
    category: 'Thermal',
    alternatives: [
      { id: 'alt-liq-1', name: 'Air Cooling', icon: '🌬️', description: 'Air cooling system', specifications: { costRange: { low: 10, high: 500, unit: 'per server' } }, advantages: ['Lower cost', 'Simpler'], disadvantages: ['Less efficient'] },
      { id: 'alt-liq-2', name: 'Immersion Cooling', icon: '🛁', description: 'Dielectric fluid immersion', specifications: { costRange: { low: 5000, high: 50000, unit: 'per tank' } }, advantages: ['Best efficiency'], disadvantages: ['Very expensive'] }
    ]
  },
  
  'Air Cooling': {
    category: 'Thermal',
    alternatives: [
      { id: 'alt-air-1', name: 'Liquid Cooling', icon: '❄️', description: 'Liquid cooling', specifications: { costRange: { low: 1000, high: 100000, unit: 'per rack' } }, advantages: ['Better cooling'], disadvantages: ['Complex'] },
      { id: 'alt-air-2', name: 'Immersion Cooling', icon: '🛁', description: 'Immersion cooling', specifications: { costRange: { low: 5000, high: 50000, unit: 'per tank' } }, advantages: ['Highest density'], disadvantages: ['Specialized'] }
    ]
  },
  
  'Heat Sinks': {
    category: 'Thermal',
    alternatives: [
      { id: 'alt-hs-1', name: 'Vapor Chamber', icon: '💨', description: 'Vapor chamber cooling', specifications: { costRange: { low: 20, high: 200, unit: 'per unit' } }, advantages: ['Better spreading'], disadvantages: ['Higher cost'] },
      { id: 'alt-hs-2', name: 'Heat Pipe', icon: '🔧', description: 'Heat pipe cooling', specifications: { costRange: { low: 10, high: 100, unit: 'per unit' } }, advantages: ['Efficient transfer'], disadvantages: ['Orientation dependent'] }
    ]
  },
  
  'Uninterruptible Power Supplies': {
    category: 'Power',
    alternatives: [
      { id: 'alt-ups-1', name: 'Generator', icon: '⚡', description: 'Backup generator', specifications: { costRange: { low: 5000, high: 50000, unit: 'per unit' } }, advantages: ['Long runtime'], disadvantages: ['Startup delay'] },
      { id: 'alt-ups-2', name: 'Fuel Cell', icon: '🔋', description: 'Hydrogen fuel cell', specifications: { costRange: { low: 10000, high: 100000, unit: 'per unit' } }, advantages: ['Clean', 'Long runtime'], disadvantages: ['Very expensive'] }
    ]
  },
  
  // ==================== LAYER 2: SENSORS & INTERFACES ====================
  
  'Camera (Visible Light)': {
    category: 'Sensor',
    alternatives: [
      { id: 'alt-cam-1', name: 'Camera (Infrared)', icon: '🌡️', description: 'Infrared camera', specifications: { costRange: { low: 500, high: 30000, unit: 'per camera' } }, advantages: ['Thermal detection'], disadvantages: ['No color'] },
      { id: 'alt-cam-2', name: 'LIDAR', icon: '📡', description: 'LIDAR for 3D', specifications: { costRange: { low: 500, high: 75000, unit: 'per unit' } }, advantages: ['3D depth'], disadvantages: ['Higher cost'] }
    ]
  },
  
  'Camera (Infrared)': {
    category: 'Sensor',
    alternatives: [
      { id: 'alt-ir-1', name: 'Camera (Visible Light)', icon: '📷', description: 'Standard camera', specifications: { costRange: { low: 50, high: 5000, unit: 'per camera' } }, advantages: ['Color', 'Lower cost'], disadvantages: ['No thermal'] },
      { id: 'alt-ir-2', name: 'Thermal Sensor Array', icon: '🌡️', description: 'Thermal array sensor', specifications: { costRange: { low: 200, high: 5000, unit: 'per unit' } }, advantages: ['Lower cost than IR camera'], disadvantages: ['Lower resolution'] }
    ]
  },
  
  'LIDAR': {
    category: 'Sensor',
    alternatives: [
      { id: 'alt-lidar-1', name: 'Camera (Visible Light)', icon: '📷', description: 'Stereo camera for depth', specifications: { costRange: { low: 100, high: 5000, unit: 'per camera' } }, advantages: ['Lower cost', 'Color'], disadvantages: ['Less accurate depth'] },
      { id: 'alt-lidar-2', name: 'Radar', icon: '📡', description: 'Radar for detection', specifications: { costRange: { low: 100, high: 1000, unit: 'per unit' } }, advantages: ['Works in all weather'], disadvantages: ['Lower resolution'] }
    ]
  },
  
  'Microphone': {
    category: 'Sensor',
    alternatives: [
      { id: 'alt-mic-1', name: 'Microphone Array', icon: '🎤', description: 'Multi-channel array', specifications: { costRange: { low: 20, high: 500, unit: 'per array' } }, advantages: ['Beamforming', 'Noise cancellation'], disadvantages: ['More complex'] },
      { id: 'alt-mic-2', name: 'Piezo Sensor', icon: '🔊', description: 'Piezo vibration sensor', specifications: { costRange: { low: 1, high: 50, unit: 'per unit' } }, advantages: ['Very cheap', 'Robust'], disadvantages: ['Limited frequency'] }
    ]
  },
  
  'Thermometer': {
    category: 'Sensor',
    alternatives: [
      { id: 'alt-therm-1', name: 'Thermocouple', icon: '🌡️', description: 'Thermocouple sensor', specifications: { costRange: { low: 1, high: 50, unit: 'per unit' } }, advantages: ['Wide range', 'Robust'], disadvantages: ['Less accurate'] },
      { id: 'alt-therm-2', name: 'RTD Sensor', icon: '🌡️', description: 'Resistance temperature detector', specifications: { costRange: { low: 5, high: 100, unit: 'per unit' } }, advantages: ['High accuracy'], disadvantages: ['Narrower range'] }
    ]
  },
  
  'Accelerometer': {
    category: 'Sensor',
    alternatives: [
      { id: 'alt-accel-1', name: 'IMU (Inertial Measurement Unit)', icon: '🧭', description: 'Combined accelerometer + gyroscope', specifications: { costRange: { low: 5, high: 500, unit: 'per unit' } }, advantages: ['More data', 'Orientation'], disadvantages: ['Higher cost'] },
      { id: 'alt-accel-2', name: 'Vibration Sensor', icon: '📳', description: 'Simple vibration sensor', specifications: { costRange: { low: 1, high: 20, unit: 'per unit' } }, advantages: ['Very cheap'], disadvantages: ['Binary output only'] }
    ]
  },
  
  'Gyroscope': {
    category: 'Sensor',
    alternatives: [
      { id: 'alt-gyro-1', name: 'IMU (Inertial Measurement Unit)', icon: '🧭', description: 'Combined IMU', specifications: { costRange: { low: 5, high: 500, unit: 'per unit' } }, advantages: ['More complete'], disadvantages: ['Higher cost'] },
      { id: 'alt-gyro-2', name: 'Magnetometer', icon: '🧲', description: 'Magnetic field sensor', specifications: { costRange: { low: 1, high: 50, unit: 'per unit' } }, advantages: ['Absolute orientation'], disadvantages: ['Magnetic interference'] }
    ]
  },
  
  'GPS': {
    category: 'Sensor',
    alternatives: [
      { id: 'alt-gps-1', name: 'GLONASS', icon: '🛰️', description: 'Russian navigation system', specifications: { costRange: { low: 5, high: 100, unit: 'per unit' } }, advantages: ['Better at high latitudes'], disadvantages: ['Less accurate'] },
      { id: 'alt-gps-2', name: 'Galileo', icon: '🛰️', description: 'European navigation', specifications: { costRange: { low: 5, high: 100, unit: 'per unit' } }, advantages: ['More accurate', 'Civilian focused'], disadvantages: ['Newer'] }
    ]
  },
  
  'Pressure Sensor': {
    category: 'Sensor',
    alternatives: [
      { id: 'alt-press-1', name: 'Barometer', icon: '🌡️', description: 'Atmospheric pressure sensor', specifications: { costRange: { low: 1, high: 50, unit: 'per unit' } }, advantages: ['Altitude measurement'], disadvantages: ['Limited range'] },
      { id: 'alt-press-2', name: 'Load Cell', icon: '⚖️', description: 'Weight/pressure measurement', specifications: { costRange: { low: 10, high: 200, unit: 'per unit' } }, advantages: ['Precise weight'], disadvantages: ['Different use case'] }
    ]
  },
  
  'Chemical/Bio Sensor': {
    category: 'Sensor',
    alternatives: [
      { id: 'alt-chem-1', name: 'Gas Sensor Array', icon: '🔬', description: 'Multi-gas detection', specifications: { costRange: { low: 20, high: 500, unit: 'per array' } }, advantages: ['Multiple gases'], disadvantages: ['Cross-sensitivity'] },
      { id: 'alt-chem-2', name: 'pH Sensor', icon: '🧪', description: 'pH measurement', specifications: { costRange: { low: 10, high: 200, unit: 'per unit' } }, advantages: ['Precise pH'], disadvantages: ['Single parameter'] }
    ]
  },
  
  'Servo Motor': {
    category: 'Actuator',
    alternatives: [
      { id: 'alt-servo-1', name: 'Stepper Motor', icon: '⚙️', description: 'Stepper motor', specifications: { costRange: { low: 10, high: 500, unit: 'per unit' } }, advantages: ['Precise positioning'], disadvantages: ['No feedback'] },
      { id: 'alt-servo-2', name: 'DC Motor with Encoder', icon: '🔧', description: 'DC motor with feedback', specifications: { costRange: { low: 20, high: 200, unit: 'per unit' } }, advantages: ['Continuous rotation'], disadvantages: ['Requires encoder'] }
    ]
  },
  
  'Stepper Motor': {
    category: 'Actuator',
    alternatives: [
      { id: 'alt-step-1', name: 'Servo Motor', icon: '⚙️', description: 'Servo motor', specifications: { costRange: { low: 10, high: 1000, unit: 'per unit' } }, advantages: ['Feedback', 'Higher torque'], disadvantages: ['Limited rotation'] },
      { id: 'alt-step-2', name: 'Linear Actuator', icon: '📏', description: 'Linear motion', specifications: { costRange: { low: 50, high: 500, unit: 'per unit' } }, advantages: ['Linear motion'], disadvantages: ['Different use case'] }
    ]
  },
  
  'Hydraulic/Pneumatic Valves': {
    category: 'Actuator',
    alternatives: [
      { id: 'alt-valve-1', name: 'Electric Actuator', icon: '⚡', description: 'Electric valve actuator', specifications: { costRange: { low: 50, high: 1000, unit: 'per unit' } }, advantages: ['Precise control'], disadvantages: ['Slower response'] },
      { id: 'alt-valve-2', name: 'Servo Motor', icon: '⚙️', description: 'Servo for valve control', specifications: { costRange: { low: 10, high: 1000, unit: 'per unit' } }, advantages: ['Fast response'], disadvantages: ['Lower force'] }
    ]
  },
  
  'Speaker': {
    category: 'Actuator',
    alternatives: [
      { id: 'alt-spk-1', name: 'Buzzer', icon: '🔔', description: 'Simple buzzer', specifications: { costRange: { low: 1, high: 10, unit: 'per unit' } }, advantages: ['Very cheap'], disadvantages: ['Limited audio'] },
      { id: 'alt-spk-2', name: 'Headphone Jack', icon: '🎧', description: 'Audio output jack', specifications: { costRange: { low: 1, high: 10, unit: 'per unit' } }, advantages: ['Private listening'], disadvantages: ['Requires headphones'] }
    ]
  },
  
  'Haptic Feedback Motor': {
    category: 'Actuator',
    alternatives: [
      { id: 'alt-hap-1', name: 'Vibration Motor', icon: '📳', description: 'Basic vibration motor', specifications: { costRange: { low: 1, high: 10, unit: 'per unit' } }, advantages: ['Very cheap'], disadvantages: ['No precision'] },
      { id: 'alt-hap-2', name: 'Piezo Actuator', icon: '🔊', description: 'Piezo haptic', specifications: { costRange: { low: 5, high: 50, unit: 'per unit' } }, advantages: ['Precise', 'Fast'], disadvantages: ['Higher cost'] }
    ]
  },
  
  'LED/Screen': {
    category: 'Actuator',
    alternatives: [
      { id: 'alt-led-1', name: 'OLED Display', icon: '📺', description: 'OLED screen', specifications: { costRange: { low: 20, high: 500, unit: 'per unit' } }, advantages: ['Better colors', 'Thinner'], disadvantages: ['Burn-in risk'] },
      { id: 'alt-led-2', name: 'E-Ink Display', icon: '📖', description: 'E-paper display', specifications: { costRange: { low: 10, high: 100, unit: 'per unit' } }, advantages: ['Low power', 'Readable'], disadvantages: ['Slow refresh'] }
    ]
  },
  
  // ==================== LAYER 3: DATA ACQUISITION ====================
  
  'Apache Kafka': {
    category: 'Data Streaming',
    alternatives: [
      { id: 'alt-kafka-1', name: 'AWS Kinesis', icon: '☁️', description: 'Managed streaming', specifications: { costRange: { low: 100, high: 10000, unit: 'per month' } }, advantages: ['Fully managed'], disadvantages: ['AWS only'] },
      { id: 'alt-kafka-2', name: 'RabbitMQ', icon: '🐰', description: 'Message broker', specifications: { costRange: { low: 0, high: 500, unit: 'per month' } }, advantages: ['Simpler', 'AMQP'], disadvantages: ['Lower throughput'] },
      { id: 'alt-kafka-3', name: 'Redis Streams', icon: '⚡', description: 'In-memory streaming', specifications: { costRange: { low: 0, high: 500, unit: 'per month' } }, advantages: ['Low latency'], disadvantages: ['Memory limited'] }
    ]
  },
  
  'AWS Kinesis': {
    category: 'Data Streaming',
    alternatives: [
      { id: 'alt-kinesis-1', name: 'Apache Kafka', icon: '📨', description: 'Self-hosted streaming', specifications: { costRange: { low: 0, high: 5000, unit: 'per month' } }, advantages: ['Self-hosted', 'More control'], disadvantages: ['Self-managed'] },
      { id: 'alt-kinesis-2', name: 'Azure Event Hubs', icon: '☁️', description: 'Azure streaming', specifications: { costRange: { low: 100, high: 5000, unit: 'per month' } }, advantages: ['Azure integration'], disadvantages: ['Azure only'] }
    ]
  },
  
  'MQTT Broker': {
    category: 'Data Streaming',
    alternatives: [
      { id: 'alt-mqtt-1', name: 'WebSocket', icon: '🔌', description: 'WebSocket streaming', specifications: { costRange: { low: 0, high: 100, unit: 'per server' } }, advantages: ['Web native'], disadvantages: ['Less IoT focused'] },
      { id: 'alt-mqtt-2', name: 'CoAP', icon: '📡', description: 'Constrained Application Protocol', specifications: { costRange: { low: 0, high: 50, unit: 'per server' } }, advantages: ['Very lightweight'], disadvantages: ['Less features'] }
    ]
  },
  
  'WebSocket': {
    category: 'Data Streaming',
    alternatives: [
      { id: 'alt-ws-1', name: 'Server-Sent Events (SSE)', icon: '📡', description: 'One-way streaming', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Simpler', 'HTTP based'], disadvantages: ['One-way only'] },
      { id: 'alt-ws-2', name: 'MQTT Broker', icon: '📡', description: 'MQTT over WebSocket', specifications: { costRange: { low: 0, high: 500, unit: 'per month' } }, advantages: ['IoT support'], disadvantages: ['Requires broker'] }
    ]
  },
  
  'Telemetry Agent': {
    category: 'Data Collection',
    alternatives: [
      { id: 'alt-tele-1', name: 'OpenTelemetry Collector', icon: '🔍', description: 'OTel collector', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Standard', 'Vendor neutral'], disadvantages: ['Newer'] },
      { id: 'alt-tele-2', name: 'Fluentd', icon: '📋', description: 'Log collector', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Mature', 'Plugins'], disadvantages: ['Ruby based'] }
    ]
  },
  
  'SQL SELECT Queries': {
    category: 'Data Extraction',
    alternatives: [
      { id: 'alt-sql-1', name: 'ETL Pipeline (Airflow)', icon: '📅', description: 'Airflow ETL', specifications: { costRange: { low: 0, high: 2000, unit: 'per month' } }, advantages: ['Automated', 'Scheduled'], disadvantages: ['More complex'] },
      { id: 'alt-sql-2', name: 'CDC (Change Data Capture)', icon: '🔄', description: 'Real-time CDC', specifications: { costRange: { low: 100, high: 1000, unit: 'per month' } }, advantages: ['Real-time'], disadvantages: ['More complex'] }
    ]
  },
  
  'ETL Pipeline (Airflow)': {
    category: 'Data Pipeline',
    alternatives: [
      { id: 'alt-airflow-1', name: 'ETL Pipeline (Dagster)', icon: '📅', description: 'Dagster pipeline', specifications: { costRange: { low: 0, high: 1000, unit: 'per month' } }, advantages: ['Type checking', 'Asset lineage'], disadvantages: ['Less mature'] },
      { id: 'alt-airflow-2', name: 'Prefect', icon: '📅', description: 'Prefect pipeline', specifications: { costRange: { low: 0, high: 1000, unit: 'per month' } }, advantages: ['Python native', 'Dynamic'], disadvantages: ['Smaller community'] }
    ]
  },
  
  'ETL Pipeline (Dagster)': {
    category: 'Data Pipeline',
    alternatives: [
      { id: 'alt-dagster-1', name: 'ETL Pipeline (Airflow)', icon: '📅', description: 'Airflow pipeline', specifications: { costRange: { low: 0, high: 2000, unit: 'per month' } }, advantages: ['Large community', 'More integrations'], disadvantages: ['Less type safety'] },
      { id: 'alt-dagster-2', name: 'Prefect', icon: '📅', description: 'Prefect pipeline', specifications: { costRange: { low: 0, high: 1000, unit: 'per month' } }, advantages: ['Simpler'], disadvantages: ['Less features'] }
    ]
  },
  
  'SFTP Download': {
    category: 'Data Transfer',
    alternatives: [
      { id: 'alt-sftp-1', name: 'HTTPS Download', icon: '🌐', description: 'HTTPS file transfer', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Universal', 'Firewall friendly'], disadvantages: ['Less secure than SFTP'] },
      { id: 'alt-sftp-2', name: 'S3 Transfer', icon: '🪣', description: 'Cloud storage transfer', specifications: { costRange: { low: 23, high: 90, unit: 'per TB/month' } }, advantages: ['Scalable'], disadvantages: ['Cloud dependency'] }
    ]
  },
  
  'Web Scraper (Scrapy)': {
    category: 'Data Collection',
    alternatives: [
      { id: 'alt-scrapy-1', name: 'Web Scraper (BeautifulSoup)', icon: '🕷️', description: 'BeautifulSoup scraper', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Simpler'], disadvantages: ['Less scalable'] },
      { id: 'alt-scrapy-2', name: 'Puppeteer', icon: '🤖', description: 'Headless browser scraping', specifications: { costRange: { low: 0, high: 100, unit: 'per month' } }, advantages: ['JavaScript rendering'], disadvantages: ['Heavier'] }
    ]
  },
  
  'Web Scraper (BeautifulSoup)': {
    category: 'Data Collection',
    alternatives: [
      { id: 'alt-bs4-1', name: 'Web Scraper (Scrapy)', icon: '🕷️', description: 'Scrapy framework', specifications: { costRange: { low: 0, high: 500, unit: 'per month' } }, advantages: ['Scalable', 'Async'], disadvantages: ['Steeper learning'] },
      { id: 'alt-bs4-2', name: 'Selenium', icon: '🌐', description: 'Browser automation', specifications: { costRange: { low: 0, high: 100, unit: 'per month' } }, advantages: ['Full browser'], disadvantages: ['Slow'] }
    ]
  },
  
  'Unity/Unreal Simulator': {
    category: 'Synthetic Data',
    alternatives: [
      { id: 'alt-unity-1', name: 'GAN Generator', icon: '🎨', description: 'GAN for synthetic data', specifications: { costRange: { low: 100, high: 10000, unit: 'per model' } }, advantages: ['Realistic images'], disadvantages: ['Requires training'] },
      { id: 'alt-unity-2', name: 'Blender', icon: '🎨', description: 'Open source 3D rendering', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Free', 'Powerful'], disadvantages: ['Less game engine features'] }
    ]
  },
  
  'GAN Generator': {
    category: 'Synthetic Data',
    alternatives: [
      { id: 'alt-gan-1', name: 'Diffusion Model', icon: '🎨', description: 'Diffusion for generation', specifications: { costRange: { low: 100, high: 50000, unit: 'per model' } }, advantages: ['Higher quality'], disadvantages: ['Slower generation'] },
      { id: 'alt-gan-2', name: 'VAE', icon: '🧬', description: 'Variational Autoencoder', specifications: { costRange: { low: 100, high: 5000, unit: 'per model' } }, advantages: ['Stable training'], disadvantages: ['Blurry output'] }
    ]
  },
  
  'Diffusion Model': {
    category: 'Synthetic Data',
    alternatives: [
      { id: 'alt-diff-1', name: 'GAN Generator', icon: '🎨', description: 'GAN for generation', specifications: { costRange: { low: 100, high: 10000, unit: 'per model' } }, advantages: ['Faster'], disadvantages: ['Less stable'] },
      { id: 'alt-diff-2', name: 'VAE', icon: '🧬', description: 'VAE for generation', specifications: { costRange: { low: 100, high: 5000, unit: 'per model' } }, advantages: ['Stable'], disadvantages: ['Lower quality'] }
    ]
  },
  
  'Procedural Generator': {
    category: 'Synthetic Data',
    alternatives: [
      { id: 'alt-proc-1', name: 'GAN Generator', icon: '🎨', description: 'GAN for procedural', specifications: { costRange: { low: 100, high: 10000, unit: 'per model' } }, advantages: ['More realistic'], disadvantages: ['Requires training'] },
      { id: 'alt-proc-2', name: 'Mock API', icon: '🔌', description: 'Mock API for data', specifications: { costRange: { low: 0, high: 50, unit: 'per month' } }, advantages: ['Quick'], disadvantages: ['Not realistic'] }
    ]
  },
  
  'Mock API': {
    category: 'Synthetic Data',
    alternatives: [
      { id: 'alt-mock-1', name: 'Procedural Generator', icon: '🔧', description: 'Procedural generation', specifications: { costRange: { low: 0, high: 1000, unit: 'per generator' } }, advantages: ['More flexible'], disadvantages: ['More code'] },
      { id: 'alt-mock-2', name: 'GAN Generator', icon: '🎨', description: 'GAN for realistic data', specifications: { costRange: { low: 100, high: 10000, unit: 'per model' } }, advantages: ['Realistic'], disadvantages: ['Complex'] }
    ]
  },
  
  'Labeling Tool (Labelbox)': {
    category: 'Data Labeling',
    alternatives: [
      { id: 'alt-labelbox-1', name: 'Labeling Tool (Scale AI)', icon: '🏷️', description: 'Scale AI labeling', specifications: { costRange: { low: 1000, high: 50000, unit: 'per month' } }, advantages: ['Managed service'], disadvantages: ['Higher cost'] },
      { id: 'alt-labelbox-2', name: 'Label Studio', icon: '🏷️', description: 'Open source labeling', specifications: { costRange: { low: 0, high: 500, unit: 'per month' } }, advantages: ['Free', 'Self-hosted'], disadvantages: ['Self-managed'] }
    ]
  },
  
  'Labeling Tool (Scale AI)': {
    category: 'Data Labeling',
    alternatives: [
      { id: 'alt-scale-1', name: 'Labeling Tool (Labelbox)', icon: '🏷️', description: 'Labelbox platform', specifications: { costRange: { low: 100, high: 10000, unit: 'per month' } }, advantages: ['More affordable'], disadvantages: ['Less managed'] },
      { id: 'alt-scale-2', name: 'Label Studio', icon: '🏷️', description: 'Open source labeling', specifications: { costRange: { low: 0, high: 500, unit: 'per month' } }, advantages: ['Free'], disadvantages: ['Self-hosted'] }
    ]
  },
  
  'Crowdsourcing (Mechanical Turk)': {
    category: 'Data Labeling',
    alternatives: [
      { id: 'alt-mturk-1', name: 'Appen', icon: '👥', description: 'Appen crowdsourcing', specifications: { costRange: { low: 100, high: 10000, unit: 'per project' } }, advantages: ['Managed'], disadvantages: ['Higher cost'] },
      { id: 'alt-mturk-2', name: 'Prolific', icon: '👥', description: 'Prolific research platform', specifications: { costRange: { low: 50, high: 5000, unit: 'per project' } }, advantages: ['Higher quality'], disadvantages: ['Smaller pool'] }
    ]
  },
  
  'Gamification': {
    category: 'Data Collection',
    alternatives: [
      { id: 'alt-gam-1', name: 'Crowdsourcing (Mechanical Turk)', icon: '👥', description: 'Paid crowdsourcing', specifications: { costRange: { low: 10, high: 1000, unit: 'per project' } }, advantages: ['Faster'], disadvantages: ['Paid'] },
      { id: 'alt-gam-2', name: 'Expert Annotation', icon: '👨‍⚕️', description: 'Expert labeling', specifications: { costRange: { low: 100, high: 10000, unit: 'per project' } }, advantages: ['High quality'], disadvantages: ['Expensive'] }
    ]
  },
  
  'Expert Annotation': {
    category: 'Data Labeling',
    alternatives: [
      { id: 'alt-expert-1', name: 'Crowdsourcing (Mechanical Turk)', icon: '👥', description: 'Crowd labeling', specifications: { costRange: { low: 10, high: 1000, unit: 'per project' } }, advantages: ['Cheaper', 'Faster'], disadvantages: ['Lower quality'] },
      { id: 'alt-expert-2', name: 'Labeling Tool (Scale AI)', icon: '🏷️', description: 'Managed expert labeling', specifications: { costRange: { low: 1000, high: 50000, unit: 'per month' } }, advantages: ['Managed quality'], disadvantages: ['Higher cost'] }
    ]
  },
  
  // ==================== LAYER 4: DATA STORAGE (Full coverage) ====================
  
  'PostgreSQL': {
    category: 'Relational Database',
    alternatives: [
      { id: 'alt-pg-1', name: 'MySQL', icon: '🐬', description: 'MySQL database', specifications: { costRange: { low: 0, high: 500, unit: 'per month' } }, advantages: ['Large community'], disadvantages: ['Fewer features'] },
      { id: 'alt-pg-2', name: 'SQLite', icon: '📱', description: 'Embedded SQL', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Zero config'], disadvantages: ['Not for production'] },
      { id: 'alt-pg-3', name: 'SQL Server', icon: '🪟', description: 'Microsoft SQL Server', specifications: { costRange: { low: 1000, high: 10000, unit: 'per license' } }, advantages: ['Enterprise features'], disadvantages: ['Licensing cost'] }
    ]
  },
  
  'MySQL': {
    category: 'Relational Database',
    alternatives: [
      { id: 'alt-mysql-1', name: 'PostgreSQL', icon: '🐘', description: 'PostgreSQL', specifications: { costRange: { low: 0, high: 1000, unit: 'per month' } }, advantages: ['More features'], disadvantages: ['Slightly more complex'] },
      { id: 'alt-mysql-2', name: 'MariaDB', icon: '🦭', description: 'MySQL fork', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Drop-in replacement'], disadvantages: ['Smaller community'] }
    ]
  },
  
  'SQL Server': {
    category: 'Relational Database',
    alternatives: [
      { id: 'alt-mssql-1', name: 'PostgreSQL', icon: '🐘', description: 'PostgreSQL', specifications: { costRange: { low: 0, high: 1000, unit: 'per month' } }, advantages: ['Free', 'Feature rich'], disadvantages: ['Migration needed'] },
      { id: 'alt-mssql-2', name: 'Oracle', icon: '🏢', description: 'Oracle Database', specifications: { costRange: { low: 5000, high: 50000, unit: 'per license' } }, advantages: ['Enterprise features'], disadvantages: ['Very expensive'] }
    ]
  },
  
  'SQLite': {
    category: 'Relational Database',
    alternatives: [
      { id: 'alt-sqlite-1', name: 'PostgreSQL', icon: '🐘', description: 'PostgreSQL', specifications: { costRange: { low: 0, high: 1000, unit: 'per month' } }, advantages: ['Full featured'], disadvantages: ['Requires server'] },
      { id: 'alt-sqlite-2', name: 'DuckDB', icon: '🦆', description: 'Analytical embedded DB', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Analytical queries'], disadvantages: ['Not for OLTP'] }
    ]
  },
  
  'Oracle': {
    category: 'Relational Database',
    alternatives: [
      { id: 'alt-oracle-1', name: 'PostgreSQL', icon: '🐘', description: 'PostgreSQL', specifications: { costRange: { low: 0, high: 1000, unit: 'per month' } }, advantages: ['Free', 'Similar features'], disadvantages: ['Migration needed'] },
      { id: 'alt-oracle-2', name: 'SQL Server', icon: '🪟', description: 'SQL Server', specifications: { costRange: { low: 1000, high: 10000, unit: 'per license' } }, advantages: ['Enterprise'], disadvantages: ['Windows-centric'] }
    ]
  },
  
  'MongoDB': {
    category: 'Document Database',
    alternatives: [
      { id: 'alt-mongo-1', name: 'Couchbase', icon: '📄', description: 'Couchbase', specifications: { costRange: { low: 0, high: 2000, unit: 'per month' } }, advantages: ['SQL + Document'], disadvantages: ['Less popular'] },
      { id: 'alt-mongo-2', name: 'Firebase Firestore', icon: '🔥', description: 'Google Firestore', specifications: { costRange: { low: 0, high: 500, unit: 'per month' } }, advantages: ['Managed'], disadvantages: ['Google only'] }
    ]
  },
  
  'AWS S3': {
    category: 'Object Storage',
    alternatives: [
      { id: 'alt-s3-1', name: 'Azure Blob Storage', icon: '☁️', description: 'Azure storage', specifications: { costRange: { low: 18, high: 80, unit: 'per TB/month' } }, advantages: ['Azure integration'], disadvantages: ['Azure only'] },
      { id: 'alt-s3-2', name: 'Google Cloud Storage', icon: '🌐', description: 'GCP storage', specifications: { costRange: { low: 20, high: 90, unit: 'per TB/month' } }, advantages: ['GCP integration'], disadvantages: ['GCP only'] },
      { id: 'alt-s3-3', name: 'MinIO', icon: '🔓', description: 'Self-hosted S3', specifications: { costRange: { low: 0, high: 500, unit: 'per month' } }, advantages: ['S3 compatible', 'No egress'], disadvantages: ['Self-managed'] }
    ]
  },
  
  'Azure Blob Storage': {
    category: 'Object Storage',
    alternatives: [
      { id: 'alt-azure-1', name: 'AWS S3', icon: '🪣', description: 'AWS S3', specifications: { costRange: { low: 23, high: 90, unit: 'per TB/month' } }, advantages: ['Most popular'], disadvantages: ['AWS only'] },
      { id: 'alt-azure-2', name: 'MinIO', icon: '🔓', description: 'Self-hosted', specifications: { costRange: { low: 0, high: 500, unit: 'per month' } }, advantages: ['Free'], disadvantages: ['Self-managed'] }
    ]
  },
  
  'Couchbase': {
    category: 'Document Database',
    alternatives: [
      { id: 'alt-couch-1', name: 'MongoDB', icon: '🍃', description: 'MongoDB', specifications: { costRange: { low: 0, high: 2000, unit: 'per month' } }, advantages: ['More popular'], disadvantages: ['No SQL'] },
      { id: 'alt-couch-2', name: 'DynamoDB', icon: '⚡', description: 'AWS DynamoDB', specifications: { costRange: { low: 0, high: 1000, unit: 'per month' } }, advantages: ['Fully managed'], disadvantages: ['AWS only'] }
    ]
  },
  
  'Firebase': {
    category: 'Document Database',
    alternatives: [
      { id: 'alt-fire-1', name: 'MongoDB', icon: '🍃', description: 'MongoDB', specifications: { costRange: { low: 0, high: 2000, unit: 'per month' } }, advantages: ['More features'], disadvantages: ['Self-managed'] },
      { id: 'alt-fire-2', name: 'Supabase', icon: '⚡', description: 'Supabase (PostgreSQL)', specifications: { costRange: { low: 0, high: 500, unit: 'per month' } }, advantages: ['SQL + Realtime'], disadvantages: ['Newer'] }
    ]
  },
  
  'Pinecone': {
    category: 'Vector Database',
    alternatives: [
      { id: 'alt-pine-1', name: 'pgvector', icon: '🔗', description: 'PostgreSQL vector', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Free', 'SQL'], disadvantages: ['Performance limits'] },
      { id: 'alt-pine-2', name: 'Weaviate', icon: '🔍', description: 'Open source vector DB', specifications: { costRange: { low: 0, high: 1000, unit: 'per month' } }, advantages: ['Open source'], disadvantages: ['Self-hosted'] },
      { id: 'alt-pine-3', name: 'Milvus', icon: '🚀', description: 'Distributed vector DB', specifications: { costRange: { low: 0, high: 1000, unit: 'per month' } }, advantages: ['Billion scale'], disadvantages: ['Complex'] }
    ]
  },
  
  'Weaviate': {
    category: 'Vector Database',
    alternatives: [
      { id: 'alt-weav-1', name: 'Pinecone', icon: '🎯', description: 'Managed vector DB', specifications: { costRange: { low: 70, high: 5000, unit: 'per month' } }, advantages: ['Fully managed'], disadvantages: ['Cost'] },
      { id: 'alt-weav-2', name: 'pgvector', icon: '🔗', description: 'PostgreSQL vector', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Free'], disadvantages: ['Less features'] }
    ]
  },
  
  'Milvus': {
    category: 'Vector Database',
    alternatives: [
      { id: 'alt-milv-1', name: 'Pinecone', icon: '🎯', description: 'Managed vector DB', specifications: { costRange: { low: 70, high: 5000, unit: 'per month' } }, advantages: ['Managed'], disadvantages: ['Cost'] },
      { id: 'alt-milv-2', name: 'FAISS', icon: '📚', description: 'Facebook similarity search', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Free', 'Fast'], disadvantages: ['No persistence'] }
    ]
  },
  
  'pgvector': {
    category: 'Vector Database',
    alternatives: [
      { id: 'alt-pgvec-1', name: 'Pinecone', icon: '🎯', description: 'Managed vector DB', specifications: { costRange: { low: 70, high: 5000, unit: 'per month' } }, advantages: ['Managed', 'Scalable'], disadvantages: ['Cost'] },
      { id: 'alt-pgvec-2', name: 'FAISS', icon: '📚', description: 'FAISS library', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Fast'], disadvantages: ['No database features'] }
    ]
  },
  
  'FAISS': {
    category: 'Vector Search',
    alternatives: [
      { id: 'alt-faiss-1', name: 'pgvector', icon: '🔗', description: 'PostgreSQL vector', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['SQL integration'], disadvantages: ['Slower'] },
      { id: 'alt-faiss-2', name: 'Pinecone', icon: '🎯', description: 'Managed vector DB', specifications: { costRange: { low: 70, high: 5000, unit: 'per month' } }, advantages: ['Managed'], disadvantages: ['Cost'] }
    ]
  },
  
  'Chroma': {
    category: 'Vector Database',
    alternatives: [
      { id: 'alt-chroma-1', name: 'Pinecone', icon: '🎯', description: 'Managed vector DB', specifications: { costRange: { low: 70, high: 5000, unit: 'per month' } }, advantages: ['Managed'], disadvantages: ['Cost'] },
      { id: 'alt-chroma-2', name: 'pgvector', icon: '🔗', description: 'PostgreSQL vector', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Free'], disadvantages: ['Less LLM focused'] }
    ]
  },
  
  'Neo4j': {
    category: 'Graph Database',
    alternatives: [
      { id: 'alt-neo-1', name: 'Amazon Neptune', icon: '☁️', description: 'AWS graph DB', specifications: { costRange: { low: 300, high: 5000, unit: 'per month' } }, advantages: ['Managed'], disadvantages: ['AWS only'] },
      { id: 'alt-neo-2', name: 'ArangoDB', icon: '🔷', description: 'Multi-model', specifications: { costRange: { low: 0, high: 1000, unit: 'per month' } }, advantages: ['Document + Graph'], disadvantages: ['Less graph features'] }
    ]
  },
  
  'Amazon Neptune': {
    category: 'Graph Database',
    alternatives: [
      { id: 'alt-neptune-1', name: 'Neo4j', icon: '🕸️', description: 'Neo4j', specifications: { costRange: { low: 0, high: 3000, unit: 'per month' } }, advantages: ['More features'], disadvantages: ['Self-managed option'] },
      { id: 'alt-neptune-2', name: 'TigerGraph', icon: '🐯', description: 'TigerGraph', specifications: { costRange: { low: 1000, high: 10000, unit: 'per month' } }, advantages: ['Real-time analytics'], disadvantages: ['Cost'] }
    ]
  },
  
  'TigerGraph': {
    category: 'Graph Database',
    alternatives: [
      { id: 'alt-tiger-1', name: 'Neo4j', icon: '🕸️', description: 'Neo4j', specifications: { costRange: { low: 0, high: 3000, unit: 'per month' } }, advantages: ['More popular'], disadvantages: ['Lower performance'] },
      { id: 'alt-tiger-2', name: 'Amazon Neptune', icon: '☁️', description: 'AWS Neptune', specifications: { costRange: { low: 300, high: 5000, unit: 'per month' } }, advantages: ['Managed'], disadvantages: ['AWS only'] }
    ]
  },
  
  'ArangoDB': {
    category: 'Multi-Model Database',
    alternatives: [
      { id: 'alt-arango-1', name: 'Neo4j', icon: '🕸️', description: 'Neo4j for graph', specifications: { costRange: { low: 0, high: 3000, unit: 'per month' } }, advantages: ['Graph focus'], disadvantages: ['No document model'] },
      { id: 'alt-arango-2', name: 'MongoDB', icon: '🍃', description: 'MongoDB for documents', specifications: { costRange: { low: 0, high: 2000, unit: 'per month' } }, advantages: ['Document focus'], disadvantages: ['No graph'] }
    ]
  },
  
  'InfluxDB': {
    category: 'Time-Series Database',
    alternatives: [
      { id: 'alt-influx-1', name: 'TimescaleDB', icon: '⏱️', description: 'PostgreSQL time-series', specifications: { costRange: { low: 0, high: 500, unit: 'per month' } }, advantages: ['SQL', 'PostgreSQL'], disadvantages: ['Less IoT focused'] },
      { id: 'alt-influx-2', name: 'Prometheus', icon: '📊', description: 'Metrics database', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Monitoring focus'], disadvantages: ['Not general purpose'] }
    ]
  },
  
  'TimescaleDB': {
    category: 'Time-Series Database',
    alternatives: [
      { id: 'alt-timescale-1', name: 'InfluxDB', icon: '📈', description: 'InfluxDB', specifications: { costRange: { low: 0, high: 1000, unit: 'per month' } }, advantages: ['IoT focus'], disadvantages: ['No SQL'] },
      { id: 'alt-timescale-2', name: 'Prometheus', icon: '📊', description: 'Prometheus', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Monitoring'], disadvantages: ['Not general purpose'] }
    ]
  },
  
  'Prometheus': {
    category: 'Monitoring Database',
    alternatives: [
      { id: 'alt-prom-1', name: 'Datadog', icon: '🐕', description: 'Datadog monitoring', specifications: { costRange: { low: 15, high: 5000, unit: 'per month' } }, advantages: ['All-in-one'], disadvantages: ['Cost'] },
      { id: 'alt-prom-2', name: 'VictoriaMetrics', icon: '📊', description: 'Prometheus compatible', specifications: { costRange: { low: 0, high: 500, unit: 'per month' } }, advantages: ['Better compression'], disadvantages: ['Less community'] }
    ]
  },
  
  'Hugging Face Hub': {
    category: 'Model Registry',
    alternatives: [
      { id: 'alt-hf-1', name: 'MLflow Registry', icon: '📦', description: 'MLflow model registry', specifications: { costRange: { low: 0, high: 1000, unit: 'per month' } }, advantages: ['Self-hosted'], disadvantages: ['Less pre-trained models'] },
      { id: 'alt-hf-2', name: 'S3 Bucket', icon: '🪣', description: 'S3 for models', specifications: { costRange: { low: 23, high: 90, unit: 'per TB/month' } }, advantages: ['Simple'], disadvantages: ['No versioning UI'] }
    ]
  },
  
  'MLflow Registry': {
    category: 'Model Registry',
    alternatives: [
      { id: 'alt-mlflow-1', name: 'Hugging Face Hub', icon: '🤗', description: 'Hugging Face', specifications: { costRange: { low: 0, high: 100, unit: 'per month' } }, advantages: ['Pre-trained models'], disadvantages: ['Cloud dependency'] },
      { id: 'alt-mlflow-2', name: 'DVC', icon: '📦', description: 'Data Version Control', specifications: { costRange: { low: 0, high: 100, unit: 'per month' } }, advantages: ['Git-based'], disadvantages: ['Less UI'] }
    ]
  },
  
  'Feature Store (Feast)': {
    category: 'Feature Store',
    alternatives: [
      { id: 'alt-feast-1', name: 'Feature Store (Tecton)', icon: '🏗️', description: 'Tecton managed', specifications: { costRange: { low: 1000, high: 10000, unit: 'per month' } }, advantages: ['Managed'], disadvantages: ['Cost'] },
      { id: 'alt-feast-2', name: 'Redis', icon: '⚡', description: 'Redis for features', specifications: { costRange: { low: 0, high: 500, unit: 'per month' } }, advantages: ['Fast'], disadvantages: ['Not feature-specific'] }
    ]
  },
  
  'Feature Store (Tecton)': {
    category: 'Feature Store',
    alternatives: [
      { id: 'alt-tecton-1', name: 'Feature Store (Feast)', icon: '🍽️', description: 'Feast open source', specifications: { costRange: { low: 0, high: 1000, unit: 'per month' } }, advantages: ['Free'], disadvantages: ['Self-managed'] },
      { id: 'alt-tecton-2', name: 'Redis', icon: '⚡', description: 'Redis for features', specifications: { costRange: { low: 0, high: 500, unit: 'per month' } }, advantages: ['Simple'], disadvantages: ['No feature logic'] }
    ]
  },
  
  'Redis': {
    category: 'Cache',
    alternatives: [
      { id: 'alt-redis-1', name: 'Memcached', icon: '💨', description: 'Memcached', specifications: { costRange: { low: 0, high: 100, unit: 'per month' } }, advantages: ['Simple'], disadvantages: ['No persistence'] },
      { id: 'alt-redis-2', name: 'Dragonfly', icon: '🐉', description: 'Dragonfly cache', specifications: { costRange: { low: 0, high: 500, unit: 'per month' } }, advantages: ['25x faster'], disadvantages: ['Newer'] }
    ]
  },
  
  'Memcached': {
    category: 'Cache',
    alternatives: [
      { id: 'alt-memc-1', name: 'Redis', icon: '⚡', description: 'Redis', specifications: { costRange: { low: 0, high: 500, unit: 'per month' } }, advantages: ['Persistence', 'More types'], disadvantages: ['Slightly slower'] },
      { id: 'alt-memc-2', name: 'Dragonfly', icon: '🐉', description: 'Dragonfly', specifications: { costRange: { low: 0, high: 500, unit: 'per month' } }, advantages: ['Fast'], disadvantages: ['Newer'] }
    ]
  },
  
  // ==================== LAYER 5: DATA PROCESSING ====================
  
  'Great Expectations': {
    category: 'Data Validation',
    alternatives: [
      { id: 'alt-ge-1', name: 'Pandera', icon: '✅', description: 'Pandera validation', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Lightweight'], disadvantages: ['Less features'] },
      { id: 'alt-ge-2', name: 'Deequ', icon: '✅', description: 'AWS Deequ', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Spark integration'], disadvantages: ['JVM based'] }
    ]
  },
  
  'Pandera': {
    category: 'Data Validation',
    alternatives: [
      { id: 'alt-pandera-1', name: 'Great Expectations', icon: '✅', description: 'Great Expectations', specifications: { costRange: { low: 0, high: 1000, unit: 'per month' } }, advantages: ['More features', 'Reporting'], disadvantages: ['Heavier'] },
      { id: 'alt-pandera-2', name: 'Pydantic', icon: '✅', description: 'Pydantic validation', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Fast', 'Python native'], disadvantages: ['Not data-focused'] }
    ]
  },
  
  'One-Hot Encoding': {
    category: 'Feature Engineering',
    alternatives: [
      { id: 'alt-ohe-1', name: 'Label Encoding', icon: '🏷️', description: 'Label encoding', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Less memory'], disadvantages: ['Implies order'] },
      { id: 'alt-ohe-2', name: 'Target Encoding', icon: '🎯', description: 'Target encoding', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Uses target info'], disadvantages: ['Overfitting risk'] }
    ]
  },
  
  'Tokenization (BPE)': {
    category: 'Text Processing',
    alternatives: [
      { id: 'alt-bpe-1', name: 'Tokenization (WordPiece)', icon: '🔤', description: 'WordPiece tokenization', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['BERT compatible'], disadvantages: ['Slightly different'] },
      { id: 'alt-bpe-2', name: 'SentencePiece', icon: '🔤', description: 'SentencePiece tokenization', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Language agnostic'], disadvantages: ['Less common'] }
    ]
  },
  
  'Tokenization (WordPiece)': {
    category: 'Text Processing',
    alternatives: [
      { id: 'alt-wp-1', name: 'Tokenization (BPE)', icon: '🔤', description: 'BPE tokenization', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['GPT compatible'], disadvantages: ['Slightly different'] },
      { id: 'alt-wp-2', name: 'SentencePiece', icon: '🔤', description: 'SentencePiece', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Universal'], disadvantages: ['Less common'] }
    ]
  },
  
  'Embedding Generation': {
    category: 'Feature Engineering',
    alternatives: [
      { id: 'alt-emb-1', name: 'TF-IDF', icon: '📊', description: 'TF-IDF vectors', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Simple', 'Fast'], disadvantages: ['No semantics'] },
      { id: 'alt-emb-2', name: 'Bag of Words', icon: '📄', description: 'BoW representation', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Very simple'], disadvantages: ['Sparse'] }
    ]
  },
  
  'Normalization': {
    category: 'Feature Engineering',
    alternatives: [
      { id: 'alt-norm-1', name: 'Standardization', icon: '📏', description: 'Z-score standardization', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Zero mean', 'Unit variance'], disadvantages: ['Not bounded'] },
      { id: 'alt-norm-2', name: 'Robust Scaling', icon: '📏', description: 'Robust scaler', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Outlier resistant'], disadvantages: ['Less common'] }
    ]
  },
  
  'Log Scaling': {
    category: 'Feature Engineering',
    alternatives: [
      { id: 'alt-log-1', name: 'Box-Cox Transform', icon: '📐', description: 'Box-Cox transformation', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['More general'], disadvantages: ['Requires parameter'] },
      { id: 'alt-log-2', name: 'Quantile Transform', icon: '📊', description: 'Quantile transformation', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Gaussian output'], disadvantages: ['Loses interpretability'] }
    ]
  },
  
  'PCA': {
    category: 'Dimensionality Reduction',
    alternatives: [
      { id: 'alt-pca-1', name: 't-SNE', icon: '📊', description: 't-SNE for visualization', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Better visualization'], disadvantages: ['Not for production'] },
      { id: 'alt-pca-2', name: 'UMAP', icon: '🗺️', description: 'UMAP reduction', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Faster', 'Preserves structure'], disadvantages: ['Newer'] }
    ]
  },
  
  't-SNE': {
    category: 'Dimensionality Reduction',
    alternatives: [
      { id: 'alt-tsne-1', name: 'PCA', icon: '📉', description: 'PCA for reduction', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Deterministic', 'Fast'], disadvantages: ['Linear only'] },
      { id: 'alt-tsne-2', name: 'UMAP', icon: '🗺️', description: 'UMAP', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Faster than t-SNE'], disadvantages: ['Newer'] }
    ]
  },
  
  'Mutual Information': {
    category: 'Feature Selection',
    alternatives: [
      { id: 'alt-mi-1', name: 'Correlation Coefficient', icon: '📊', description: 'Pearson correlation', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Simple'], disadvantages: ['Linear only'] },
      { id: 'alt-mi-2', name: 'Chi-Square Test', icon: '📊', description: 'Chi-square for categorical', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Categorical'], disadvantages: ['Less general'] }
    ]
  },
  
  'Recursive Feature Elimination': {
    category: 'Feature Selection',
    alternatives: [
      { id: 'alt-rfe-1', name: 'Lasso Regularization', icon: '🎯', description: 'L1 regularization', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Built-in'], disadvantages: ['Linear only'] },
      { id: 'alt-rfe-2', name: 'Feature Importance', icon: '📊', description: 'Tree-based importance', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Model specific'], disadvantages: ['Requires tree model'] }
    ]
  },
  
  'Lasso Regularization': {
    category: 'Feature Selection',
    alternatives: [
      { id: 'alt-lasso-1', name: 'Ridge Regularization', icon: '🎯', description: 'L2 regularization', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Stable'], disadvantages: ['No feature selection'] },
      { id: 'alt-lasso-2', name: 'Elastic Net', icon: '🎯', description: 'L1+L2 regularization', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Best of both'], disadvantages: ['Two parameters'] }
    ]
  },
  
  'Image Rotation/Cropping': {
    category: 'Data Augmentation',
    alternatives: [
      { id: 'alt-img-aug-1', name: 'Color Jitter', icon: '🎨', description: 'Color augmentation', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Color invariance'], disadvantages: ['No geometric'] },
      { id: 'alt-img-aug-2', name: 'MixUp', icon: '🔄', description: 'MixUp augmentation', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Regularization'], disadvantages: ['Less interpretable'] }
    ]
  },
  
  'SMOTE': {
    category: 'Data Augmentation',
    alternatives: [
      { id: 'alt-smote-1', name: 'Random Oversampling', icon: '⚖️', description: 'Random oversampling', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Simple'], disadvantages: ['Overfitting'] },
      { id: 'alt-smote-2', name: 'ADASYN', icon: '⚖️', description: 'Adaptive synthetic sampling', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Focuses on hard cases'], disadvantages: ['More complex'] }
    ]
  },
  
  'Text Back-translation': {
    category: 'Data Augmentation',
    alternatives: [
      { id: 'alt-bt-1', name: 'Synonym Replacement', icon: '📝', description: 'Synonym augmentation', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Simple'], disadvantages: ['Limited variety'] },
      { id: 'alt-bt-2', name: 'Text Paraphrasing', icon: '📝', description: 'LLM paraphrasing', specifications: { costRange: { low: 0, high: 100, unit: 'per month' } }, advantages: ['More natural'], disadvantages: ['API cost'] }
    ]
  },
  
  'Audio Noise Injection': {
    category: 'Data Augmentation',
    alternatives: [
      { id: 'alt-audio-aug-1', name: 'Time Stretching', icon: '🎵', description: 'Time stretch augmentation', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Speed invariance'], disadvantages: ['Less natural'] },
      { id: 'alt-audio-aug-2', name: 'Pitch Shifting', icon: '🎵', description: 'Pitch shift augmentation', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Pitch invariance'], disadvantages: ['Can distort'] }
    ]
  },
  
  'Prompt Templates': {
    category: 'LLM Engineering',
    alternatives: [
      { id: 'alt-prompt-1', name: 'Few-Shot Examples', icon: '📝', description: 'Few-shot prompting', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Better performance'], disadvantages: ['More tokens'] },
      { id: 'alt-prompt-2', name: 'Chain-of-Thought', icon: '🧠', description: 'CoT prompting', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Better reasoning'], disadvantages: ['Slower'] }
    ]
  },
  
  'RAG Pipelines': {
    category: 'LLM Engineering',
    alternatives: [
      { id: 'alt-rag-1', name: 'Fine-tuning', icon: '🎯', description: 'Model fine-tuning', specifications: { costRange: { low: 100, high: 10000, unit: 'per model' } }, advantages: ['No retrieval needed'], disadvantages: ['Expensive', 'Stale knowledge'] },
      { id: 'alt-rag-2', name: 'Prompt Engineering', icon: '📝', description: 'Pure prompting', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Simple'], disadvantages: ['Limited knowledge'] }
    ]
  },
  
  'Metadata Filtering': {
    category: 'LLM Engineering',
    alternatives: [
      { id: 'alt-meta-1', name: 'Semantic Search', icon: '🔍', description: 'Embedding-based search', specifications: { costRange: { low: 0, high: 100, unit: 'per month' } }, advantages: ['More flexible'], disadvantages: ['Less precise'] },
      { id: 'alt-meta-2', name: 'Hybrid Search', icon: '🔍', description: 'BM25 + Vector', specifications: { costRange: { low: 0, high: 200, unit: 'per month' } }, advantages: ['Best accuracy'], disadvantages: ['More complex'] }
    ]
  },
  
  // ==================== LAYER 6: MODEL COMPONENTS (Full) ====================
  
  'Expert System': {
    category: 'Symbolic AI',
    alternatives: [
      { id: 'alt-es-1', name: 'Decision Tree', icon: '🌳', description: 'Decision tree model', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Interpretable'], disadvantages: ['Less flexible'] },
      { id: 'alt-es-2', name: 'Rule Engine (Drools)', icon: '⚙️', description: 'Drools rule engine', specifications: { costRange: { low: 0, high: 1000, unit: 'per month' } }, advantages: ['Scalable rules'], disadvantages: ['Java based'] }
    ]
  },
  
  'Decision Tree': {
    category: 'Classical ML',
    alternatives: [
      { id: 'alt-dt-1', name: 'Random Forest', icon: '🌲', description: 'Random forest ensemble', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Higher accuracy'], disadvantages: ['Less interpretable'] },
      { id: 'alt-dt-2', name: 'XGBoost', icon: '🚀', description: 'Gradient boosting', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Best accuracy'], disadvantages: ['More tuning'] }
    ]
  },
  
  'Rule Engine (Drools)': {
    category: 'Symbolic AI',
    alternatives: [
      { id: 'alt-drools-1', name: 'Expert System', icon: '📚', description: 'Custom expert system', specifications: { costRange: { low: 1000, high: 100000, unit: 'per system' } }, advantages: ['Full control'], disadvantages: ['Custom code'] },
      { id: 'alt-drools-2', name: 'Decision Tree', icon: '🌳', description: 'Decision tree', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Simple'], disadvantages: ['Less rules'] }
    ]
  },
  
  'State Machine': {
    category: 'Symbolic AI',
    alternatives: [
      { id: 'alt-sm-1', name: 'Decision Tree', icon: '🌳', description: 'Decision tree for states', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Learnable'], disadvantages: ['Less control'] },
      { id: 'alt-sm-2', name: 'Expert System', icon: '📚', description: 'Expert system', specifications: { costRange: { low: 1000, high: 100000, unit: 'per system' } }, advantages: ['Complex logic'], disadvantages: ['Expensive'] }
    ]
  },
  
  'Ontology': {
    category: 'Symbolic AI',
    alternatives: [
      { id: 'alt-onto-1', name: 'Neo4j', icon: '🕸️', description: 'Graph database for knowledge', specifications: { costRange: { low: 0, high: 3000, unit: 'per month' } }, advantages: ['Scalable'], disadvantages: ['Less formal'] },
      { id: 'alt-onto-2', name: 'Knowledge Graph', icon: '📚', description: 'Knowledge graph', specifications: { costRange: { low: 0, high: 5000, unit: 'per month' } }, advantages: ['Flexible'], disadvantages: ['Complex'] }
    ]
  },
  
  'Linear Regression': {
    category: 'Classical ML',
    alternatives: [
      { id: 'alt-lr-1', name: 'Ridge Regression', icon: '📈', description: 'Regularized linear', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Less overfitting'], disadvantages: ['Bias'] },
      { id: 'alt-lr-2', name: 'XGBoost', icon: '🚀', description: 'Gradient boosting', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Non-linear'], disadvantages: ['Less interpretable'] }
    ]
  },
  
  'Logistic Regression': {
    category: 'Classical ML',
    alternatives: [
      { id: 'alt-logr-1', name: 'SVM', icon: '✂️', description: 'Support Vector Machine', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Better for high-dim'], disadvantages: ['Less probabilistic'] },
      { id: 'alt-logr-2', name: 'XGBoost', icon: '🚀', description: 'Gradient boosting', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Higher accuracy'], disadvantages: ['Less interpretable'] }
    ]
  },
  
  'SVM': {
    category: 'Classical ML',
    alternatives: [
      { id: 'alt-svm-1', name: 'Logistic Regression', icon: '📊', description: 'Logistic regression', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Probabilistic', 'Fast'], disadvantages: ['Linear only'] },
      { id: 'alt-svm-2', name: 'Random Forest', icon: '🌲', description: 'Random forest', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Non-linear', 'Robust'], disadvantages: ['Less interpretable'] }
    ]
  },
  
  'K-Nearest Neighbors': {
    category: 'Classical ML',
    alternatives: [
      { id: 'alt-knn-1', name: 'Radius Neighbors', icon: '👥', description: 'Radius-based neighbors', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Variable neighbors'], disadvantages: ['Radius tuning'] },
      { id: 'alt-knn-2', name: 'XGBoost', icon: '🚀', description: 'Gradient boosting', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Better for large data'], disadvantages: ['Less interpretable'] }
    ]
  },
  
  'Random Forest': {
    category: 'Classical ML',
    alternatives: [
      { id: 'alt-rf-1', name: 'XGBoost', icon: '🚀', description: 'Gradient boosting', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Higher accuracy'], disadvantages: ['More tuning'] },
      { id: 'alt-rf-2', name: 'Extra Trees', icon: '🌳', description: 'Extremely randomized trees', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Faster'], disadvantages: ['More bias'] }
    ]
  },
  
  'XGBoost': {
    category: 'Classical ML',
    alternatives: [
      { id: 'alt-xgb-1', name: 'LightGBM', icon: '💡', description: 'Microsoft GBM', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Faster', 'Less memory'], disadvantages: ['Can overfit'] },
      { id: 'alt-xgb-2', name: 'CatBoost', icon: '🐱', description: 'Yandex GBM', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Categorical native'], disadvantages: ['Slower'] }
    ]
  },
  
  'Naive Bayes': {
    category: 'Classical ML',
    alternatives: [
      { id: 'alt-nb-1', name: 'Logistic Regression', icon: '📊', description: 'Logistic regression', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Better calibration'], disadvantages: ['Less simple'] },
      { id: 'alt-nb-2', name: 'SVM', icon: '✂️', description: 'SVM for text', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Better for high-dim'], disadvantages: ['Less interpretable'] }
    ]
  },
  
  'CNN': {
    category: 'Deep Learning',
    alternatives: [
      { id: 'alt-cnn-1', name: 'Vision Transformer (ViT)', icon: '👁️', description: 'Transformer for vision', specifications: { costRange: { low: 100, high: 10000, unit: 'per training' } }, advantages: ['Better long-range'], disadvantages: ['More data'] },
      { id: 'alt-cnn-2', name: 'EfficientNet', icon: '⚡', description: 'Efficient CNN', specifications: { costRange: { low: 0, high: 1000, unit: 'per training' } }, advantages: ['Efficient'], disadvantages: ['Less transfer'] }
    ]
  },
  
  'RNN': {
    category: 'Deep Learning',
    alternatives: [
      { id: 'alt-rnn-1', name: 'LSTM', icon: '🔁', description: 'LSTM', specifications: { costRange: { low: 0, high: 500, unit: 'per training' } }, advantages: ['Better memory'], disadvantages: ['Slower'] },
      { id: 'alt-rnn-2', name: 'Transformer', icon: '🧠', description: 'Transformer', specifications: { costRange: { low: 100, high: 1000000, unit: 'per training' } }, advantages: ['Parallel', 'Better'], disadvantages: ['More compute'] }
    ]
  },
  
  'LSTM': {
    category: 'Deep Learning',
    alternatives: [
      { id: 'alt-lstm-1', name: 'GRU', icon: '🔁', description: 'Gated Recurrent Unit', specifications: { costRange: { low: 0, high: 500, unit: 'per training' } }, advantages: ['Simpler', 'Faster'], disadvantages: ['Less expressive'] },
      { id: 'alt-lstm-2', name: 'Transformer', icon: '🧠', description: 'Transformer', specifications: { costRange: { low: 100, high: 1000000, unit: 'per training' } }, advantages: ['Better long-range'], disadvantages: ['More compute'] }
    ]
  },
  
  'Transformer': {
    category: 'Deep Learning',
    alternatives: [
      { id: 'alt-trans-1', name: 'Mamba (SSM)', icon: '🐍', description: 'State Space Model', specifications: { costRange: { low: 100, high: 10000, unit: 'per training' } }, advantages: ['Linear scaling'], disadvantages: ['Newer'] },
      { id: 'alt-trans-2', name: 'LSTM', icon: '🔁', description: 'LSTM for sequences', specifications: { costRange: { low: 0, high: 500, unit: 'per training' } }, advantages: ['Lower compute'], disadvantages: ['Limited context'] },
      { id: 'alt-trans-3', name: 'Mixture of Experts (MoE)', icon: '👥', description: 'Sparse expert model', specifications: { costRange: { low: 1000, high: 1000000, unit: 'per training' } }, advantages: ['Efficient'], disadvantages: ['Complex'] }
    ]
  },
  
  'GAN': {
    category: 'Deep Learning',
    alternatives: [
      { id: 'alt-gan-1', name: 'Diffusion Model', icon: '🎨', description: 'Diffusion model', specifications: { costRange: { low: 100, high: 50000, unit: 'per training' } }, advantages: ['Higher quality'], disadvantages: ['Slower'] },
      { id: 'alt-gan-2', name: 'VAE', icon: '🧬', description: 'Variational Autoencoder', specifications: { costRange: { low: 100, high: 5000, unit: 'per training' } }, advantages: ['Stable'], disadvantages: ['Blurry'] }
    ]
  },
  
  'VAE': {
    category: 'Deep Learning',
    alternatives: [
      { id: 'alt-vae-1', name: 'GAN', icon: '🎨', description: 'GAN', specifications: { costRange: { low: 100, high: 10000, unit: 'per training' } }, advantages: ['Sharper'], disadvantages: ['Unstable'] },
      { id: 'alt-vae-2', name: 'Diffusion Model', icon: '🎨', description: 'Diffusion', specifications: { costRange: { low: 100, high: 50000, unit: 'per training' } }, advantages: ['Best quality'], disadvantages: ['Slow'] }
    ]
  },
  
  'Diffusion Model': {
    category: 'Deep Learning',
    alternatives: [
      { id: 'alt-dm-1', name: 'GAN', icon: '🎨', description: 'GAN', specifications: { costRange: { low: 100, high: 10000, unit: 'per training' } }, advantages: ['Faster'], disadvantages: ['Less stable'] },
      { id: 'alt-dm-2', name: 'VAE', icon: '🧬', description: 'VAE', specifications: { costRange: { low: 100, high: 5000, unit: 'per training' } }, advantages: ['Stable'], disadvantages: ['Lower quality'] }
    ]
  },
  
  'Graph Neural Network': {
    category: 'Deep Learning',
    alternatives: [
      { id: 'alt-gnn-1', name: 'Neo4j', icon: '🕸️', description: 'Graph database', specifications: { costRange: { low: 0, high: 3000, unit: 'per month' } }, advantages: ['Scalable'], disadvantages: ['No learning'] },
      { id: 'alt-gnn-2', name: 'Node2Vec', icon: '🔗', description: 'Node embedding', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Simple'], disadvantages: ['Less expressive'] }
    ]
  },
  
  'DQN': {
    category: 'Reinforcement Learning',
    alternatives: [
      { id: 'alt-dqn-1', name: 'PPO', icon: '🎮', description: 'PPO', specifications: { costRange: { low: 100, high: 10000, unit: 'per training' } }, advantages: ['Continuous actions'], disadvantages: ['More complex'] },
      { id: 'alt-dqn-2', name: 'A3C', icon: '🎮', description: 'A3C', specifications: { costRange: { low: 100, high: 5000, unit: 'per training' } }, advantages: ['Parallel'], disadvantages: ['Complex'] }
    ]
  },
  
  'PPO': {
    category: 'Reinforcement Learning',
    alternatives: [
      { id: 'alt-ppo-1', name: 'DQN', icon: '🎮', description: 'DQN', specifications: { costRange: { low: 100, high: 5000, unit: 'per training' } }, advantages: ['Simpler'], disadvantages: ['Discrete only'] },
      { id: 'alt-ppo-2', name: 'SAC', icon: '🎮', description: 'Soft Actor-Critic', specifications: { costRange: { low: 100, high: 5000, unit: 'per training' } }, advantages: ['Sample efficient'], disadvantages: ['More hyperparameters'] }
    ]
  },
  
  'A3C': {
    category: 'Reinforcement Learning',
    alternatives: [
      { id: 'alt-a3c-1', name: 'PPO', icon: '🎮', description: 'PPO', specifications: { costRange: { low: 100, high: 10000, unit: 'per training' } }, advantages: ['More stable'], disadvantages: ['Slower'] },
      { id: 'alt-a3c-2', name: 'DQN', icon: '🎮', description: 'DQN', specifications: { costRange: { low: 100, high: 5000, unit: 'per training' } }, advantages: ['Simpler'], disadvantages: ['Discrete'] }
    ]
  },
  
  'Actor-Critic Methods': {
    category: 'Reinforcement Learning',
    alternatives: [
      { id: 'alt-ac-1', name: 'PPO', icon: '🎮', description: 'PPO', specifications: { costRange: { low: 100, high: 10000, unit: 'per training' } }, advantages: ['Modern'], disadvantages: ['Complex'] },
      { id: 'alt-ac-2', name: 'DQN', icon: '🎮', description: 'DQN', specifications: { costRange: { low: 100, high: 5000, unit: 'per training' } }, advantages: ['Simpler'], disadvantages: ['Value only'] }
    ]
  },
  
  'Multi-Armed Bandit': {
    category: 'Reinforcement Learning',
    alternatives: [
      { id: 'alt-mab-1', name: 'A/B Testing', icon: '🧪', description: 'A/B testing', specifications: { costRange: { low: 0, high: 1000, unit: 'per month' } }, advantages: ['Simple'], disadvantages: ['Slower'] },
      { id: 'alt-mab-2', name: 'Thompson Sampling', icon: '🎲', description: 'Bayesian bandit', specifications: { costRange: { low: 0, high: 100, unit: 'per implementation' } }, advantages: ['Optimal'], disadvantages: ['Complex'] }
    ]
  },
  
  'A* Search': {
    category: 'Search Algorithm',
    alternatives: [
      { id: 'alt-astar-1', name: 'Dijkstra', icon: '🗺️', description: "Dijkstra's algorithm", specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Simpler'], disadvantages: ['No heuristic'] },
      { id: 'alt-astar-2', name: 'BFS', icon: '🔍', description: 'Breadth-First Search', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Simple'], disadvantages: ['No weights'] }
    ]
  },
  
  'Monte Carlo Tree Search': {
    category: 'Search Algorithm',
    alternatives: [
      { id: 'alt-mcts-1', name: 'A* Search', icon: '🗺️', description: 'A* search', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Deterministic'], disadvantages: ['No exploration'] },
      { id: 'alt-mcts-2', name: 'Minimax', icon: '🎮', description: 'Minimax algorithm', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Optimal'], disadvantages: ['Expensive'] }
    ]
  },
  
  'Genetic Algorithm': {
    category: 'Optimization',
    alternatives: [
      { id: 'alt-ga-1', name: 'Simulated Annealing', icon: '🌡️', description: 'Simulated annealing', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Global search'], disadvantages: ['Slower'] },
      { id: 'alt-ga-2', name: 'Particle Swarm', icon: '🐦', description: 'Particle swarm', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Continuous'], disadvantages: ['Less discrete'] }
    ]
  },
  
  'Simulated Annealing': {
    category: 'Optimization',
    alternatives: [
      { id: 'alt-sa-1', name: 'Genetic Algorithm', icon: '🧬', description: 'Genetic algorithm', specifications: { costRange: { low: 0, high: 100, unit: 'per run' } }, advantages: ['Population'], disadvantages: ['More parameters'] },
      { id: 'alt-sa-2', name: 'Hill Climbing', icon: '⛰️', description: 'Hill climbing', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Simple'], disadvantages: ['Local minima'] }
    ]
  },
  
  'Particle Swarm': {
    category: 'Optimization',
    alternatives: [
      { id: 'alt-pso-1', name: 'Genetic Algorithm', icon: '🧬', description: 'Genetic algorithm', specifications: { costRange: { low: 0, high: 100, unit: 'per run' } }, advantages: ['Discrete support'], disadvantages: ['More parameters'] },
      { id: 'alt-pso-2', name: 'Simulated Annealing', icon: '🌡️', description: 'Simulated annealing', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Global'], disadvantages: ['Slower'] }
    ]
  },
  
  'Bagging': {
    category: 'Ensemble',
    alternatives: [
      { id: 'alt-bag-1', name: 'Boosting', icon: '📈', description: 'Boosting', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Lower bias'], disadvantages: ['Can overfit'] },
      { id: 'alt-bag-2', name: 'Stacking', icon: '📚', description: 'Stacking', specifications: { costRange: { low: 0, high: 100, unit: 'per implementation' } }, advantages: ['Combines models'], disadvantages: ['Complex'] }
    ]
  },
  
  'Boosting': {
    category: 'Ensemble',
    alternatives: [
      { id: 'alt-boost-1', name: 'Bagging', icon: '🎒', description: 'Bagging', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Lower variance'], disadvantages: ['Higher bias'] },
      { id: 'alt-boost-2', name: 'Stacking', icon: '📚', description: 'Stacking', specifications: { costRange: { low: 0, high: 100, unit: 'per implementation' } }, advantages: ['Best of both'], disadvantages: ['Complex'] }
    ]
  },
  
  'Stacking': {
    category: 'Ensemble',
    alternatives: [
      { id: 'alt-stack-1', name: 'Boosting', icon: '📈', description: 'Boosting', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Sequential improvement'], disadvantages: ['Can overfit'] },
      { id: 'alt-stack-2', name: 'Blending', icon: '🔀', description: 'Blending', specifications: { costRange: { low: 0, high: 100, unit: 'per implementation' } }, advantages: ['Simpler'], disadvantages: ['Less data for meta'] }
    ]
  },
  
  'Model Cascades': {
    category: 'Ensemble',
    alternatives: [
      { id: 'alt-cascade-1', name: 'Mixture of Experts (MoE)', icon: '👥', description: 'MoE', specifications: { costRange: { low: 1000, high: 1000000, unit: 'per training' } }, advantages: ['Learned routing'], disadvantages: ['Complex'] },
      { id: 'alt-cascade-2', name: 'Knowledge Distillation', icon: '🎓', description: 'Distillation', specifications: { costRange: { low: 0, high: 100, unit: 'per model' } }, advantages: ['Single model'], disadvantages: ['Less dynamic'] }
    ]
  },
  
  'Mixture of Experts (MoE)': {
    category: 'Ensemble',
    alternatives: [
      { id: 'alt-moe-1', name: 'Model Cascades', icon: '🌊', description: 'Model cascade', specifications: { costRange: { low: 0, high: 1000, unit: 'per month' } }, advantages: ['Simpler'], disadvantages: ['Manual routing'] },
      { id: 'alt-moe-2', name: 'Ensemble Averaging', icon: '📊', description: 'Simple averaging', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Simple'], disadvantages: ['Less efficient'] }
    ]
  },
  
  'Few-Shot Learner': {
    category: 'Meta-Learning',
    alternatives: [
      { id: 'alt-fsl-1', name: 'Fine-tuning', icon: '🎯', description: 'Model fine-tuning', specifications: { costRange: { low: 100, high: 10000, unit: 'per model' } }, advantages: ['Better performance'], disadvantages: ['More data'] },
      { id: 'alt-fsl-2', name: 'Zero-Shot Learning', icon: '🔮', description: 'Zero-shot', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['No examples'], disadvantages: ['Lower accuracy'] }
    ]
  },
  
  // ==================== LAYER 7: OPTIMIZATION ====================
  
  'Cross-Entropy': {
    category: 'Loss Function',
    alternatives: [
      { id: 'alt-ce-1', name: 'Focal Loss', icon: '📊', description: 'Focal loss', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Class imbalance'], disadvantages: ['Extra parameter'] },
      { id: 'alt-ce-2', name: 'Hinge Loss', icon: '📊', description: 'Hinge loss (SVM)', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Margin'], disadvantages: ['Not probabilistic'] }
    ]
  },
  
  'MSE': {
    category: 'Loss Function',
    alternatives: [
      { id: 'alt-mse-1', name: 'Huber Loss', icon: '📈', description: 'Huber loss', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Outlier robust'], disadvantages: ['Extra parameter'] },
      { id: 'alt-mse-2', name: 'MAE', icon: '📈', description: 'Mean Absolute Error', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Outlier robust'], disadvantages: ['Non-smooth'] }
    ]
  },
  
  'Huber Loss': {
    category: 'Loss Function',
    alternatives: [
      { id: 'alt-huber-1', name: 'MSE', icon: '📈', description: 'MSE', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Smooth'], disadvantages: ['Outlier sensitive'] },
      { id: 'alt-huber-2', name: 'MAE', icon: '📈', description: 'MAE', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Robust'], disadvantages: ['Non-smooth at 0'] }
    ]
  },
  
  'Contrastive Loss': {
    category: 'Loss Function',
    alternatives: [
      { id: 'alt-contrast-1', name: 'Triplet Loss', icon: '📊', description: 'Triplet loss', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Better embeddings'], disadvantages: ['Hard mining'] },
      { id: 'alt-contrast-2', name: 'InfoNCE', icon: '📊', description: 'InfoNCE loss', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Self-supervised'], disadvantages: ['Batch size dependent'] }
    ]
  },
  
  'KL Divergence': {
    category: 'Loss Function',
    alternatives: [
      { id: 'alt-kl-1', name: 'Wasserstein Distance', icon: '📊', description: 'Wasserstein metric', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Better gradients'], disadvantages: ['Complex'] },
      { id: 'alt-kl-2', name: 'JS Divergence', icon: '📊', description: 'Jensen-Shannon', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Symmetric'], disadvantages: ['Still limited'] }
    ]
  },
  
  'CTC Loss': {
    category: 'Loss Function',
    alternatives: [
      { id: 'alt-ctc-1', name: 'RNN-T Loss', icon: '📊', description: 'RNN Transducer', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Online'], disadvantages: ['Complex'] },
      { id: 'alt-ctc-2', name: 'Attention Loss', icon: '📊', description: 'Attention-based', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['No alignment'], disadvantages: ['Slower'] }
    ]
  },
  
  'SGD': {
    category: 'Optimizer',
    alternatives: [
      { id: 'alt-sgd-1', name: 'Adam', icon: '⚙️', description: 'Adam optimizer', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Adaptive', 'Fast'], disadvantages: ['Less generalization'] },
      { id: 'alt-sgd-2', name: 'SGD with Momentum', icon: '⚙️', description: 'SGD + momentum', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Better convergence'], disadvantages: ['Extra parameter'] }
    ]
  },
  
  'Adam': {
    category: 'Optimizer',
    alternatives: [
      { id: 'alt-adam-1', name: 'AdamW', icon: '⚙️', description: 'Adam with decoupled WD', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Better regularization'], disadvantages: ['Slightly different'] },
      { id: 'alt-adam-2', name: 'Lion', icon: '🦁', description: 'Lion optimizer', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Memory efficient'], disadvantages: ['Newer'] },
      { id: 'alt-adam-3', name: 'RAdam', icon: '⚙️', description: 'Rectified Adam', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Stable warmup'], disadvantages: ['Less common'] }
    ]
  },
  
  'RMSprop': {
    category: 'Optimizer',
    alternatives: [
      { id: 'alt-rms-1', name: 'Adam', icon: '⚙️', description: 'Adam', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Momentum'], disadvantages: ['More memory'] },
      { id: 'alt-rms-2', name: 'Adagrad', icon: '⚙️', description: 'Adagrad', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Sparse'], disadvantages: ['LR decay'] }
    ]
  },
  
  'Adagrad': {
    category: 'Optimizer',
    alternatives: [
      { id: 'alt-adagrad-1', name: 'RMSprop', icon: '⚙️', description: 'RMSprop', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['No LR decay'], disadvantages: ['Less sparse'] },
      { id: 'alt-adagrad-2', name: 'Adam', icon: '⚙️', description: 'Adam', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Momentum'], disadvantages: ['More memory'] }
    ]
  },
  
  'L-BFGS': {
    category: 'Optimizer',
    alternatives: [
      { id: 'alt-lbfgs-1', name: 'Adam', icon: '⚙️', description: 'Adam', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Works for large'], disadvantages: ['Less precise'] },
      { id: 'alt-lbfgs-2', name: 'SGD', icon: '⚙️', description: 'SGD', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Simple'], disadvantages: ['Slower'] }
    ]
  },
  
  'Lion': {
    category: 'Optimizer',
    alternatives: [
      { id: 'alt-lion-1', name: 'AdamW', icon: '⚙️', description: 'AdamW', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['More established'], disadvantages: ['More memory'] },
      { id: 'alt-lion-2', name: 'Sophia', icon: '⚙️', description: 'Sophia optimizer', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Faster'], disadvantages: ['Newer'] }
    ]
  },
  
  'Bradley-Terry Model': {
    category: 'RLHF',
    alternatives: [
      { id: 'alt-bt-1', name: 'Elo Rating', icon: '🏆', description: 'Elo rating system', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Simple'], disadvantages: ['Less formal'] },
      { id: 'alt-bt-2', name: 'Plackett-Luce', icon: '🏆', description: 'Plackett-Luce model', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Multi-way'], disadvantages: ['Complex'] }
    ]
  },
  
  'PPO (for RLHF)': {
    category: 'RLHF',
    alternatives: [
      { id: 'alt-ppo-rlhf-1', name: 'DPO', icon: '🎯', description: 'Direct Preference Optimization', specifications: { costRange: { low: 100, high: 10000, unit: 'per model' } }, advantages: ['Simpler'], disadvantages: ['Less stable'] },
      { id: 'alt-ppo-rlhf-2', name: 'Constitutional AI', icon: '📜', description: 'Constitutional AI', specifications: { costRange: { low: 1000, high: 50000, unit: 'per model' } }, advantages: ['Safer'], disadvantages: ['Complex'] }
    ]
  },
  
  'DPO': {
    category: 'RLHF',
    alternatives: [
      { id: 'alt-dpo-1', name: 'PPO (for RLHF)', icon: '🎮', description: 'PPO for RLHF', specifications: { costRange: { low: 1000, high: 100000, unit: 'per model' } }, advantages: ['More stable'], disadvantages: ['More compute'] },
      { id: 'alt-dpo-2', name: 'KTO', icon: '🎯', description: 'Kahneman-Tversky Optimization', specifications: { costRange: { low: 100, high: 10000, unit: 'per model' } }, advantages: ['Uses binary feedback'], disadvantages: ['Newer'] }
    ]
  },
  
  'Constitutional AI': {
    category: 'RLHF',
    alternatives: [
      { id: 'alt-cai-1', name: 'RLHF Pipeline', icon: '🤝', description: 'Standard RLHF', specifications: { costRange: { low: 1000, high: 100000, unit: 'per model' } }, advantages: ['Proven'], disadvantages: ['Requires human feedback'] },
      { id: 'alt-cai-2', name: 'DPO', icon: '🎯', description: 'DPO', specifications: { costRange: { low: 100, high: 10000, unit: 'per model' } }, advantages: ['Simpler'], disadvantages: ['Less safe'] }
    ]
  },
  
  'Action Masking (RL)': {
    category: 'Constraint',
    alternatives: [
      { id: 'alt-am-1', name: 'Output Validators', icon: '🛡️', description: 'Output validation', specifications: { costRange: { low: 0, high: 100, unit: 'per month' } }, advantages: ['Post-hoc'], disadvantages: ['Less efficient'] },
      { id: 'alt-am-2', name: 'Guardrails', icon: '🛡️', description: 'Guardrails', specifications: { costRange: { low: 100, high: 5000, unit: 'per month' } }, advantages: ['Comprehensive'], disadvantages: ['Overhead'] }
    ]
  },
  
  'Output Validators': {
    category: 'Constraint',
    alternatives: [
      { id: 'alt-ov-1', name: 'Action Masking (RL)', icon: '🎭', description: 'Action masking', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Pre-emptive'], disadvantages: ['RL specific'] },
      { id: 'alt-ov-2', name: 'Safety Classifiers', icon: '🛡️', description: 'Safety classification', specifications: { costRange: { low: 100, high: 1000, unit: 'per month' } }, advantages: ['General'], disadvantages: ['Can miss'] }
    ]
  },
  
  'Guardrails': {
    category: 'Safety',
    alternatives: [
      { id: 'alt-guard-1', name: 'Safety Classifiers', icon: '🛡️', description: 'Safety classifiers', specifications: { costRange: { low: 100, high: 1000, unit: 'per month' } }, advantages: ['Simpler'], disadvantages: ['Less comprehensive'] },
      { id: 'alt-guard-2', name: 'Prompt Injection Firewall', icon: '🔥', description: 'Prompt firewall', specifications: { costRange: { low: 100, high: 1000, unit: 'per month' } }, advantages: ['Targeted'], disadvantages: ['Only prompts'] }
    ]
  },
  
  'Safety Classifiers': {
    category: 'Safety',
    alternatives: [
      { id: 'alt-sc-1', name: 'Guardrails', icon: '🛡️', description: 'Guardrails framework', specifications: { costRange: { low: 100, high: 5000, unit: 'per month' } }, advantages: ['Comprehensive'], disadvantages: ['Overhead'] },
      { id: 'alt-sc-2', name: 'Moderation API', icon: '🛡️', description: 'Third-party moderation', specifications: { costRange: { low: 100, high: 1000, unit: 'per month' } }, advantages: ['Managed'], disadvantages: ['Cost'] }
    ]
  },
  
  'Learning Rate Scheduler': {
    category: 'Training',
    alternatives: [
      { id: 'alt-lrs-1', name: 'Cyclic LR', icon: '📊', description: 'Cyclical learning rate', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Faster'], disadvantages: ['Less predictable'] },
      { id: 'alt-lrs-2', name: 'Adaptive LR', icon: '📊', description: 'Adaptive methods', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Automatic'], disadvantages: ['Less control'] }
    ]
  },
  
  'Batch Size Logic': {
    category: 'Training',
    alternatives: [
      { id: 'alt-bs-1', name: 'Gradient Accumulation', icon: '📦', description: 'Gradient accumulation', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Memory efficient'], disadvantages: ['Slower'] },
      { id: 'alt-bs-2', name: 'Dynamic Batching', icon: '📦', description: 'Dynamic batch sizing', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Optimal'], disadvantages: ['Complex'] }
    ]
  },
  
  'Dropout Rates': {
    category: 'Training',
    alternatives: [
      { id: 'alt-drop-1', name: 'DropConnect', icon: '🎲', description: 'DropConnect', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['More general'], disadvantages: ['Less common'] },
      { id: 'alt-drop-2', name: 'Stochastic Depth', icon: '🎲', description: 'Stochastic depth', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['For deep networks'], disadvantages: ['Complex'] }
    ]
  },
  
  'Early Stopping Criteria': {
    category: 'Training',
    alternatives: [
      { id: 'alt-es-1', name: 'Model Checkpointing', icon: '💾', description: 'Save best model', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Always best'], disadvantages: ['Storage'] },
      { id: 'alt-es-2', name: 'Regularization', icon: '🎯', description: 'Strong regularization', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Prevents overfit'], disadvantages: ['Less training'] }
    ]
  },
  
  // ==================== LAYER 8: EXECUTION ENGINE ====================
  
  'ONNX Runtime': {
    category: 'Runtime',
    alternatives: [
      { id: 'alt-onnx-1', name: 'TensorRT', icon: '🚀', description: 'NVIDIA TensorRT', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Faster on NVIDIA'], disadvantages: ['NVIDIA only'] },
      { id: 'alt-onnx-2', name: 'OpenVINO', icon: '🔧', description: 'Intel OpenVINO', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Intel optimized'], disadvantages: ['Intel only'] }
    ]
  },
  
  'TensorRT': {
    category: 'Runtime',
    alternatives: [
      { id: 'alt-tr-1', name: 'ONNX Runtime', icon: '🔧', description: 'ONNX Runtime', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Cross-platform'], disadvantages: ['Less NVIDIA optimized'] },
      { id: 'alt-tr-2', name: 'Apache TVM', icon: '📺', description: 'TVM compiler', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Hardware agnostic'], disadvantages: ['Complex'] }
    ]
  },
  
  'OpenVINO': {
    category: 'Runtime',
    alternatives: [
      { id: 'alt-ov-1', name: 'ONNX Runtime', icon: '🔧', description: 'ONNX Runtime', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Cross-platform'], disadvantages: ['Less Intel optimized'] },
      { id: 'alt-ov-2', name: 'TensorRT', icon: '🚀', description: 'TensorRT', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['NVIDIA'], disadvantages: ['NVIDIA only'] }
    ]
  },
  
  'XLA': {
    category: 'Compiler',
    alternatives: [
      { id: 'alt-xla-1', name: 'ONNX Runtime', icon: '🔧', description: 'ONNX Runtime', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Cross-framework'], disadvantages: ['Less TensorFlow specific'] },
      { id: 'alt-xla-2', name: 'TensorRT', icon: '🚀', description: 'TensorRT', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['NVIDIA'], disadvantages: ['NVIDIA only'] }
    ]
  },
  
  'Core ML': {
    category: 'Runtime',
    alternatives: [
      { id: 'alt-coreml-1', name: 'TensorFlow Lite', icon: '📱', description: 'TF Lite', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Cross-platform'], disadvantages: ['Less Apple optimized'] },
      { id: 'alt-coreml-2', name: 'ONNX Mobile', icon: '📱', description: 'ONNX for mobile', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Cross-platform'], disadvantages: ['Less optimized'] }
    ]
  },
  
  'PyTorch': {
    category: 'Framework',
    alternatives: [
      { id: 'alt-pt-1', name: 'TensorFlow', icon: '📦', description: 'TensorFlow', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Production', 'TPU'], disadvantages: ['Less Pythonic'] },
      { id: 'alt-pt-2', name: 'JAX', icon: '⚡', description: 'JAX', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Performance'], disadvantages: ['Learning curve'] }
    ]
  },
  
  'TensorFlow': {
    category: 'Framework',
    alternatives: [
      { id: 'alt-tf-1', name: 'PyTorch', icon: '🔥', description: 'PyTorch', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Research', 'Pythonic'], disadvantages: ['Less production'] },
      { id: 'alt-tf-2', name: 'JAX', icon: '⚡', description: 'JAX', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Performance'], disadvantages: ['Learning curve'] }
    ]
  },
  
  'JAX': {
    category: 'Framework',
    alternatives: [
      { id: 'alt-jax-1', name: 'PyTorch', icon: '🔥', description: 'PyTorch', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Easier'], disadvantages: ['Slower'] },
      { id: 'alt-jax-2', name: 'TensorFlow', icon: '📦', description: 'TensorFlow', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Production'], disadvantages: ['Less flexible'] }
    ]
  },
  
  'Scikit-learn': {
    category: 'Framework',
    alternatives: [
      { id: 'alt-sklearn-1', name: 'XGBoost', icon: '🚀', description: 'XGBoost', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Better accuracy'], disadvantages: ['Less algorithms'] },
      { id: 'alt-sklearn-2', name: 'LightGBM', icon: '💡', description: 'LightGBM', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Faster'], disadvantages: ['Less algorithms'] }
    ]
  },
  
  'Hugging Face Transformers': {
    category: 'Framework',
    alternatives: [
      { id: 'alt-hft-1', name: 'PyTorch', icon: '🔥', description: 'Raw PyTorch', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['More control'], disadvantages: ['More code'] },
      { id: 'alt-hft-2', name: 'spaCy', icon: '📝', description: 'spaCy for NLP', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Production NLP'], disadvantages: ['Less LLMs'] }
    ]
  },
  
  'Keras': {
    category: 'Framework',
    alternatives: [
      { id: 'alt-keras-1', name: 'PyTorch', icon: '🔥', description: 'PyTorch', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['More flexible'], disadvantages: ['Less abstract'] },
      { id: 'alt-keras-2', name: 'FastAI', icon: '🚀', description: 'FastAI', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Simpler'], disadvantages: ['Less control'] }
    ]
  },
  
  'Quantization (FP16)': {
    category: 'Optimization',
    alternatives: [
      { id: 'alt-fp16-1', name: 'Quantization (INT8)', icon: '📏', description: 'INT8 quantization', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Smaller'], disadvantages: ['Less precise'] },
      { id: 'alt-fp16-2', name: 'BFloat16', icon: '📏', description: 'BFloat16 quantization', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Better range'], disadvantages: ['Less precise mantissa'] }
    ]
  },
  
  'Quantization (INT8)': {
    category: 'Optimization',
    alternatives: [
      { id: 'alt-int8-1', name: 'Quantization (FP16)', icon: '📏', description: 'FP16 quantization', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['More precise'], disadvantages: ['Larger'] },
      { id: 'alt-int8-2', name: 'Quantization (INT4)', icon: '📏', description: 'INT4 quantization', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Smallest'], disadvantages: ['Less precise'] }
    ]
  },
  
  'Pruning': {
    category: 'Optimization',
    alternatives: [
      { id: 'alt-prune-1', name: 'Knowledge Distillation', icon: '🎓', description: 'Distillation', specifications: { costRange: { low: 0, high: 100, unit: 'per model' } }, advantages: ['Better quality'], disadvantages: ['Requires teacher'] },
      { id: 'alt-prune-2', name: 'Weight Clustering', icon: '🔗', description: 'Weight clustering', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Simple'], disadvantages: ['Less reduction'] }
    ]
  },
  
  'Knowledge Distillation': {
    category: 'Optimization',
    alternatives: [
      { id: 'alt-kd-1', name: 'Pruning', icon: '✂️', description: 'Network pruning', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['No teacher'], disadvantages: ['Less quality'] },
      { id: 'alt-kd-2', name: 'Quantization (INT8)', icon: '📏', description: 'INT8 quantization', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Simple'], disadvantages: ['Less reduction'] }
    ]
  },
  
  'Weight Clustering': {
    category: 'Optimization',
    alternatives: [
      { id: 'alt-wc-1', name: 'Pruning', icon: '✂️', description: 'Pruning', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['More reduction'], disadvantages: ['Quality loss'] },
      { id: 'alt-wc-2', name: 'Quantization (INT8)', icon: '📏', description: 'INT8', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Simple'], disadvantages: ['Fixed reduction'] }
    ]
  },
  
  'Docker': {
    category: 'Containerization',
    alternatives: [
      { id: 'alt-docker-1', name: 'Podman', icon: '📦', description: 'Podman', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Rootless'], disadvantages: ['Less tooling'] },
      { id: 'alt-docker-2', name: 'Containerd', icon: '📦', description: 'Containerd', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['K8s standard'], disadvantages: ['Lower level'] }
    ]
  },
  
  'Podman': {
    category: 'Containerization',
    alternatives: [
      { id: 'alt-podman-1', name: 'Docker', icon: '🐳', description: 'Docker', specifications: { costRange: { low: 0, high: 21, unit: 'per month' } }, advantages: ['More tools'], disadvantages: ['Daemon'] },
      { id: 'alt-podman-2', name: 'Containerd', icon: '📦', description: 'Containerd', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Standard'], disadvantages: ['Lower level'] }
    ]
  },
  
  'Conda': {
    category: 'Environment',
    alternatives: [
      { id: 'alt-conda-1', name: 'Virtualenv', icon: '🐍', description: 'Virtualenv', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Lightweight'], disadvantages: ['Python only'] },
      { id: 'alt-conda-2', name: 'Poetry', icon: '📦', description: 'Poetry', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Dependency resolution'], disadvantages: ['Python only'] }
    ]
  },
  
  'Virtualenv': {
    category: 'Environment',
    alternatives: [
      { id: 'alt-venv-1', name: 'Conda', icon: '🐍', description: 'Conda', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Non-Python'], disadvantages: ['Heavier'] },
      { id: 'alt-venv-2', name: 'Poetry', icon: '📦', description: 'Poetry', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Better deps'], disadvantages: ['Newer'] }
    ]
  },
  
  'PyPI/NPM Packages': {
    category: 'Package Management',
    alternatives: [
      { id: 'alt-pypi-1', name: 'Conda', icon: '🐍', description: 'Conda packages', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Binary'], disadvantages: ['Fewer packages'] },
      { id: 'alt-pypi-2', name: 'Docker Images', icon: '🐳', description: 'Container images', specifications: { costRange: { low: 0, high: 21, unit: 'per month' } }, advantages: ['Complete env'], disadvantages: ['Larger'] }
    ]
  },
  
  'Real-time predict() API': {
    category: 'Execution',
    alternatives: [
      { id: 'alt-rt-1', name: 'Batch Scoring Scripts', icon: '📦', description: 'Batch scoring', specifications: { costRange: { low: 1, high: 100, unit: 'per run' } }, advantages: ['Cost effective'], disadvantages: ['Not real-time'] },
      { id: 'alt-rt-2', name: 'Streaming Inference', icon: '🌊', description: 'Streaming inference', specifications: { costRange: { low: 100, high: 1000, unit: 'per month' } }, advantages: ['Continuous'], disadvantages: ['Complex'] }
    ]
  },
  
  'Batch Scoring Scripts': {
    category: 'Execution',
    alternatives: [
      { id: 'alt-batch-1', name: 'Real-time predict() API', icon: '⚡', description: 'Real-time API', specifications: { costRange: { low: 10, high: 1000, unit: 'per month' } }, advantages: ['Immediate'], disadvantages: ['Higher cost'] },
      { id: 'alt-batch-2', name: 'Streaming Inference', icon: '🌊', description: 'Streaming', specifications: { costRange: { low: 100, high: 1000, unit: 'per month' } }, advantages: ['Continuous'], disadvantages: ['Complex'] }
    ]
  },
  
  'Streaming Inference': {
    category: 'Execution',
    alternatives: [
      { id: 'alt-stream-1', name: 'Real-time predict() API', icon: '⚡', description: 'Real-time API', specifications: { costRange: { low: 10, high: 1000, unit: 'per month' } }, advantages: ['Simpler'], disadvantages: ['Not continuous'] },
      { id: 'alt-stream-2', name: 'Batch Scoring Scripts', icon: '📦', description: 'Batch scoring', specifications: { costRange: { low: 1, high: 100, unit: 'per run' } }, advantages: ['Cost effective'], disadvantages: ['Not real-time'] }
    ]
  },
  
  'On-device Execution': {
    category: 'Execution',
    alternatives: [
      { id: 'alt-ondev-1', name: 'Real-time predict() API', icon: '⚡', description: 'Cloud API', specifications: { costRange: { low: 10, high: 1000, unit: 'per month' } }, advantages: ['More compute'], disadvantages: ['Latency'] },
      { id: 'alt-ondev-2', name: 'Batch Scoring Scripts', icon: '📦', description: 'Offline batch', specifications: { costRange: { low: 1, high: 100, unit: 'per run' } }, advantages: ['Cost'], disadvantages: ['Not real-time'] }
    ]
  },
  
  // ==================== LAYER 9: ORCHESTRATION ====================
  
  'Apache Airflow': {
    category: 'Workflow',
    alternatives: [
      { id: 'alt-airflow-1', name: 'Prefect', icon: '📅', description: 'Prefect', specifications: { costRange: { low: 0, high: 1000, unit: 'per month' } }, advantages: ['Python native'], disadvantages: ['Less mature'] },
      { id: 'alt-airflow-2', name: 'AWS Step Functions', icon: '☁️', description: 'AWS Step Functions', specifications: { costRange: { low: 0, high: 100, unit: 'per month' } }, advantages: ['Serverless'], disadvantages: ['AWS only'] }
    ]
  },
  
  'Dagster': {
    category: 'Workflow',
    alternatives: [
      { id: 'alt-dagster-1', name: 'Apache Airflow', icon: '📅', description: 'Airflow', specifications: { costRange: { low: 0, high: 2000, unit: 'per month' } }, advantages: ['More integrations'], disadvantages: ['Less type safety'] },
      { id: 'alt-dagster-2', name: 'Prefect', icon: '📅', description: 'Prefect', specifications: { costRange: { low: 0, high: 1000, unit: 'per month' } }, advantages: ['Simpler'], disadvantages: ['Less features'] }
    ]
  },
  
  'Prefect': {
    category: 'Workflow',
    alternatives: [
      { id: 'alt-prefect-1', name: 'Apache Airflow', icon: '📅', description: 'Airflow', specifications: { costRange: { low: 0, high: 2000, unit: 'per month' } }, advantages: ['Mature'], disadvantages: ['Less dynamic'] },
      { id: 'alt-prefect-2', name: 'Dagster', icon: '📅', description: 'Dagster', specifications: { costRange: { low: 0, high: 1000, unit: 'per month' } }, advantages: ['Type safety'], disadvantages: ['Newer'] }
    ]
  },
  
  'Kubeflow Pipelines': {
    category: 'Workflow',
    alternatives: [
      { id: 'alt-kubeflow-1', name: 'Apache Airflow', icon: '📅', description: 'Airflow', specifications: { costRange: { low: 0, high: 2000, unit: 'per month' } }, advantages: ['More general'], disadvantages: ['Less ML specific'] },
      { id: 'alt-kubeflow-2', name: 'MLflow Pipelines', icon: '📦', description: 'MLflow Pipelines', specifications: { costRange: { low: 0, high: 1000, unit: 'per month' } }, advantages: ['ML focus'], disadvantages: ['Less flexible'] }
    ]
  },
  
  'AWS Step Functions': {
    category: 'Workflow',
    alternatives: [
      { id: 'alt-sfn-1', name: 'Apache Airflow', icon: '📅', description: 'Airflow', specifications: { costRange: { low: 0, high: 2000, unit: 'per month' } }, advantages: ['Self-hosted'], disadvantages: ['More management'] },
      { id: 'alt-sfn-2', name: 'Azure Logic Apps', icon: '☁️', description: 'Azure Logic Apps', specifications: { costRange: { low: 0, high: 500, unit: 'per month' } }, advantages: ['Azure'], disadvantages: ['Azure only'] }
    ]
  },
  
  'Kubernetes': {
    category: 'Orchestration',
    alternatives: [
      { id: 'alt-k8s-1', name: 'Docker Swarm', icon: '🐳', description: 'Docker Swarm', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Simple'], disadvantages: ['Less features'] },
      { id: 'alt-k8s-2', name: 'Nomad', icon: '🏗️', description: 'Nomad', specifications: { costRange: { low: 0, high: 1000, unit: 'per month' } }, advantages: ['Simple'], disadvantages: ['Smaller ecosystem'] },
      { id: 'alt-k8s-3', name: 'AWS ECS', icon: '☁️', description: 'AWS ECS', specifications: { costRange: { low: 0, high: 1000, unit: 'per month' } }, advantages: ['Managed'], disadvantages: ['AWS only'] }
    ]
  },
  
  'Docker Swarm': {
    category: 'Orchestration',
    alternatives: [
      { id: 'alt-swarm-1', name: 'Kubernetes', icon: '☸️', description: 'Kubernetes', specifications: { costRange: { low: 100, high: 10000, unit: 'per month' } }, advantages: ['More features'], disadvantages: ['Complex'] },
      { id: 'alt-swarm-2', name: 'Nomad', icon: '🏗️', description: 'Nomad', specifications: { costRange: { low: 0, high: 1000, unit: 'per month' } }, advantages: ['Simple'], disadvantages: ['Less Docker integration'] }
    ]
  },
  
  'Nomad': {
    category: 'Orchestration',
    alternatives: [
      { id: 'alt-nomad-1', name: 'Kubernetes', icon: '☸️', description: 'Kubernetes', specifications: { costRange: { low: 100, high: 10000, unit: 'per month' } }, advantages: ['More features'], disadvantages: ['Complex'] },
      { id: 'alt-nomad-2', name: 'Docker Swarm', icon: '🐳', description: 'Docker Swarm', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Simple'], disadvantages: ['Less features'] }
    ]
  },
  
  'AWS ECS': {
    category: 'Orchestration',
    alternatives: [
      { id: 'alt-ecs-1', name: 'Kubernetes', icon: '☸️', description: 'Kubernetes', specifications: { costRange: { low: 100, high: 10000, unit: 'per month' } }, advantages: ['Portable'], disadvantages: ['More management'] },
      { id: 'alt-ecs-2', name: 'Azure AKS', icon: '☁️', description: 'Azure Kubernetes', specifications: { costRange: { low: 100, high: 5000, unit: 'per month' } }, advantages: ['Azure'], disadvantages: ['Azure only'] }
    ]
  },
  
  'RabbitMQ': {
    category: 'Messaging',
    alternatives: [
      { id: 'alt-rabbit-1', name: 'Apache Kafka', icon: '📨', description: 'Kafka', specifications: { costRange: { low: 0, high: 5000, unit: 'per month' } }, advantages: ['Higher throughput'], disadvantages: ['Complex'] },
      { id: 'alt-rabbit-2', name: 'AWS SQS', icon: '☁️', description: 'AWS SQS', specifications: { costRange: { low: 0, high: 100, unit: 'per month' } }, advantages: ['Managed'], disadvantages: ['AWS only'] },
      { id: 'alt-rabbit-3', name: 'Redis Streams', icon: '⚡', description: 'Redis Streams', specifications: { costRange: { low: 0, high: 500, unit: 'per month' } }, advantages: ['Low latency'], disadvantages: ['Memory limited'] }
    ]
  },
  
  'AWS SQS': {
    category: 'Messaging',
    alternatives: [
      { id: 'alt-sqs-1', name: 'RabbitMQ', icon: '🐰', description: 'RabbitMQ', specifications: { costRange: { low: 0, high: 500, unit: 'per month' } }, advantages: ['Self-hosted'], disadvantages: ['More management'] },
      { id: 'alt-sqs-2', name: 'Apache Kafka', icon: '📨', description: 'Kafka', specifications: { costRange: { low: 0, high: 5000, unit: 'per month' } }, advantages: ['Streaming'], disadvantages: ['Complex'] }
    ]
  },
  
  'Redis Streams': {
    category: 'Messaging',
    alternatives: [
      { id: 'alt-rs-1', name: 'Apache Kafka', icon: '📨', description: 'Kafka', specifications: { costRange: { low: 0, high: 5000, unit: 'per month' } }, advantages: ['Durable'], disadvantages: ['Complex'] },
      { id: 'alt-rs-2', name: 'RabbitMQ', icon: '🐰', description: 'RabbitMQ', specifications: { costRange: { low: 0, high: 500, unit: 'per month' } }, advantages: ['AMQP'], disadvantages: ['Slower'] }
    ]
  },
  
  'GitHub Actions': {
    category: 'CI/CD',
    alternatives: [
      { id: 'alt-gha-1', name: 'Jenkins', icon: '🏗️', description: 'Jenkins', specifications: { costRange: { low: 0, high: 1000, unit: 'per month' } }, advantages: ['Self-hosted'], disadvantages: ['More management'] },
      { id: 'alt-gha-2', name: 'GitLab CI', icon: '🔄', description: 'GitLab CI', specifications: { costRange: { low: 0, high: 100, unit: 'per month' } }, advantages: ['Integrated'], disadvantages: ['GitLab only'] },
      { id: 'alt-gha-3', name: 'ArgoCD', icon: '🚢', description: 'ArgoCD', specifications: { costRange: { low: 0, high: 100, unit: 'per month' } }, advantages: ['GitOps'], disadvantages: ['K8s only'] }
    ]
  },
  
  'Jenkins': {
    category: 'CI/CD',
    alternatives: [
      { id: 'alt-jenkins-1', name: 'GitHub Actions', icon: '🔄', description: 'GitHub Actions', specifications: { costRange: { low: 0, high: 50, unit: 'per month' } }, advantages: ['Managed'], disadvantages: ['GitHub only'] },
      { id: 'alt-jenkins-2', name: 'GitLab CI', icon: '🔄', description: 'GitLab CI', specifications: { costRange: { low: 0, high: 100, unit: 'per month' } }, advantages: ['Integrated'], disadvantages: ['GitLab only'] }
    ]
  },
  
  'GitLab CI': {
    category: 'CI/CD',
    alternatives: [
      { id: 'alt-glci-1', name: 'GitHub Actions', icon: '🔄', description: 'GitHub Actions', specifications: { costRange: { low: 0, high: 50, unit: 'per month' } }, advantages: ['GitHub'], disadvantages: ['GitHub only'] },
      { id: 'alt-glci-2', name: 'Jenkins', icon: '🏗️', description: 'Jenkins', specifications: { costRange: { low: 0, high: 1000, unit: 'per month' } }, advantages: ['Self-hosted'], disadvantages: ['More management'] }
    ]
  },
  
  'ArgoCD': {
    category: 'CI/CD',
    alternatives: [
      { id: 'alt-argocd-1', name: 'Flux', icon: '🚢', description: 'Flux CD', specifications: { costRange: { low: 0, high: 100, unit: 'per month' } }, advantages: ['GitOps'], disadvantages: ['K8s only'] },
      { id: 'alt-argocd-2', name: 'Jenkins', icon: '🏗️', description: 'Jenkins', specifications: { costRange: { low: 0, high: 1000, unit: 'per month' } }, advantages: ['General'], disadvantages: ['Not GitOps'] }
    ]
  },
  
  'Nginx': {
    category: 'Serving',
    alternatives: [
      { id: 'alt-nginx-1', name: 'Envoy Proxy', icon: '🔀', description: 'Envoy', specifications: { costRange: { low: 0, high: 1000, unit: 'per month' } }, advantages: ['Service mesh'], disadvantages: ['Complex'] },
      { id: 'alt-nginx-2', name: 'HAProxy', icon: '⚖️', description: 'HAProxy', specifications: { costRange: { low: 0, high: 500, unit: 'per month' } }, advantages: ['Load balancing'], disadvantages: ['Less features'] }
    ]
  },
  
  'Envoy Proxy': {
    category: 'Serving',
    alternatives: [
      { id: 'alt-envoy-1', name: 'Nginx', icon: '🌐', description: 'Nginx', specifications: { costRange: { low: 0, high: 500, unit: 'per month' } }, advantages: ['Simple'], disadvantages: ['Less mesh'] },
      { id: 'alt-envoy-2', name: 'HAProxy', icon: '⚖️', description: 'HAProxy', specifications: { costRange: { low: 0, high: 500, unit: 'per month' } }, advantages: ['Performance'], disadvantages: ['Less features'] }
    ]
  },
  
  'API Gateway (Kong)': {
    category: 'Serving',
    alternatives: [
      { id: 'alt-kong-1', name: 'Nginx', icon: '🌐', description: 'Nginx', specifications: { costRange: { low: 0, high: 500, unit: 'per month' } }, advantages: ['Simple'], disadvantages: ['Less API features'] },
      { id: 'alt-kong-2', name: 'AWS API Gateway', icon: '☁️', description: 'AWS API Gateway', specifications: { costRange: { low: 0, high: 500, unit: 'per month' } }, advantages: ['Managed'], disadvantages: ['AWS only'] }
    ]
  },
  
  'Load Balancer': {
    category: 'Serving',
    alternatives: [
      { id: 'alt-lb-1', name: 'Nginx', icon: '🌐', description: 'Nginx LB', specifications: { costRange: { low: 0, high: 500, unit: 'per month' } }, advantages: ['Simple'], disadvantages: ['Less advanced'] },
      { id: 'alt-lb-2', name: 'HAProxy', icon: '⚖️', description: 'HAProxy', specifications: { costRange: { low: 0, high: 500, unit: 'per month' } }, advantages: ['Performance'], disadvantages: ['Less features'] },
      { id: 'alt-lb-3', name: 'AWS ELB', icon: '☁️', description: 'AWS ELB', specifications: { costRange: { low: 0, high: 500, unit: 'per month' } }, advantages: ['Managed'], disadvantages: ['AWS only'] }
    ]
  },
  
  'Serverless Functions (Lambda)': {
    category: 'Serving',
    alternatives: [
      { id: 'alt-lambda-1', name: 'Kubernetes', icon: '☸️', description: 'Kubernetes', specifications: { costRange: { low: 100, high: 10000, unit: 'per month' } }, advantages: ['More control'], disadvantages: ['More management'] },
      { id: 'alt-lambda-2', name: 'Google Cloud Functions', icon: '☁️', description: 'GCP Functions', specifications: { costRange: { low: 0, high: 100, unit: 'per month' } }, advantages: ['GCP'], disadvantages: ['GCP only'] }
    ]
  },
  
  // ==================== LAYER 10: APPLICATION ====================
  
  'Python (FastAPI)': {
    category: 'Backend',
    alternatives: [
      { id: 'alt-fastapi-1', name: 'Python (Django)', icon: '🐍', description: 'Django', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Full featured'], disadvantages: ['Slower'] },
      { id: 'alt-fastapi-2', name: 'Node.js', icon: '🟢', description: 'Node.js', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['JavaScript'], disadvantages: ['Less ML'] },
      { id: 'alt-fastapi-3', name: 'Go', icon: '🐹', description: 'Go', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Performance'], disadvantages: ['Less ML'] }
    ]
  },
  
  'Python (Django)': {
    category: 'Backend',
    alternatives: [
      { id: 'alt-django-1', name: 'Python (FastAPI)', icon: '⚡', description: 'FastAPI', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Faster'], disadvantages: ['Less features'] },
      { id: 'alt-django-2', name: 'Flask', icon: '🍶', description: 'Flask', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Simple'], disadvantages: ['Less features'] }
    ]
  },
  
  'Node.js': {
    category: 'Backend',
    alternatives: [
      { id: 'alt-node-1', name: 'Python (FastAPI)', icon: '🐍', description: 'FastAPI', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['ML ecosystem'], disadvantages: ['Slower'] },
      { id: 'alt-node-2', name: 'Go', icon: '🐹', description: 'Go', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Performance'], disadvantages: ['Less packages'] }
    ]
  },
  
  'Go': {
    category: 'Backend',
    alternatives: [
      { id: 'alt-go-1', name: 'Python (FastAPI)', icon: '🐍', description: 'FastAPI', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['ML ecosystem'], disadvantages: ['Slower'] },
      { id: 'alt-go-2', name: 'Node.js', icon: '🟢', description: 'Node.js', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['JavaScript'], disadvantages: ['Less performant'] }
    ]
  },
  
  'Business Logic Middleware': {
    category: 'Backend',
    alternatives: [
      { id: 'alt-blm-1', name: 'Python (FastAPI)', icon: '🐍', description: 'FastAPI middleware', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Simple'], disadvantages: ['Less specialized'] },
      { id: 'alt-blm-2', name: 'Apache Camel', icon: '🐫', description: 'Apache Camel', specifications: { costRange: { low: 0, high: 1000, unit: 'per month' } }, advantages: ['Integration'], disadvantages: ['Java'] }
    ]
  },
  
  'React': {
    category: 'Frontend',
    alternatives: [
      { id: 'alt-react-1', name: 'Vue.js', icon: '💚', description: 'Vue.js', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Simpler'], disadvantages: ['Smaller ecosystem'] },
      { id: 'alt-react-2', name: 'Svelte', icon: '🔥', description: 'Svelte', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Fastest'], disadvantages: ['Newer'] },
      { id: 'alt-react-3', name: 'Angular', icon: '🅰️', description: 'Angular', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Complete'], disadvantages: ['Complex'] }
    ]
  },
  
  'Vue.js': {
    category: 'Frontend',
    alternatives: [
      { id: 'alt-vue-1', name: 'React', icon: '⚛️', description: 'React', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Larger ecosystem'], disadvantages: ['More complex'] },
      { id: 'alt-vue-2', name: 'Svelte', icon: '🔥', description: 'Svelte', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Faster'], disadvantages: ['Newer'] }
    ]
  },
  
  'HTML/CSS': {
    category: 'Frontend',
    alternatives: [
      { id: 'alt-html-1', name: 'React', icon: '⚛️', description: 'React', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Dynamic'], disadvantages: ['More complex'] },
      { id: 'alt-html-2', name: 'Tailwind CSS', icon: '🎨', description: 'Tailwind CSS', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Utility-first'], disadvantages: ['Learning curve'] }
    ]
  },
  
  'Streamlit': {
    category: 'Frontend',
    alternatives: [
      { id: 'alt-streamlit-1', name: 'Gradio', icon: '🎨', description: 'Gradio', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['ML focused'], disadvantages: ['Less customizable'] },
      { id: 'alt-streamlit-2', name: 'Dash', icon: '📊', description: 'Plotly Dash', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['More flexible'], disadvantages: ['More code'] }
    ]
  },
  
  'Gradio': {
    category: 'Frontend',
    alternatives: [
      { id: 'alt-gradio-1', name: 'Streamlit', icon: '📊', description: 'Streamlit', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['More general'], disadvantages: ['Less ML focused'] },
      { id: 'alt-gradio-2', name: 'Dash', icon: '📊', description: 'Plotly Dash', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Flexible'], disadvantages: ['More code'] }
    ]
  },
  
  'Power BI': {
    category: 'Visualization',
    alternatives: [
      { id: 'alt-pbi-1', name: 'Tableau', icon: '📈', description: 'Tableau', specifications: { costRange: { low: 70, high: 2000, unit: 'per month' } }, advantages: ['Better visuals'], disadvantages: ['Higher cost'] },
      { id: 'alt-pbi-2', name: 'Grafana', icon: '📊', description: 'Grafana', specifications: { costRange: { low: 0, high: 1000, unit: 'per month' } }, advantages: ['Free', 'Real-time'], disadvantages: ['Less BI features'] }
    ]
  },
  
  'Tableau': {
    category: 'Visualization',
    alternatives: [
      { id: 'alt-tableau-1', name: 'Power BI', icon: '📊', description: 'Power BI', specifications: { costRange: { low: 10, high: 5000, unit: 'per month' } }, advantages: ['Microsoft integration'], disadvantages: ['Less visuals'] },
      { id: 'alt-tableau-2', name: 'Grafana', icon: '📊', description: 'Grafana', specifications: { costRange: { low: 0, high: 1000, unit: 'per month' } }, advantages: ['Free'], disadvantages: ['Less BI'] }
    ]
  },
  
  'Custom Dashboard': {
    category: 'Visualization',
    alternatives: [
      { id: 'alt-cdash-1', name: 'Grafana', icon: '📊', description: 'Grafana', specifications: { costRange: { low: 0, high: 1000, unit: 'per month' } }, advantages: ['Free', 'Plugins'], disadvantages: ['Less custom'] },
      { id: 'alt-cdash-2', name: 'Power BI', icon: '📊', description: 'Power BI', specifications: { costRange: { low: 10, high: 5000, unit: 'per month' } }, advantages: ['Managed'], disadvantages: ['Cost'] }
    ]
  },
  
  'Chatbot': {
    category: 'Conversational',
    alternatives: [
      { id: 'alt-chatbot-1', name: 'Voice Assistant', icon: '🎙️', description: 'Voice assistant', specifications: { costRange: { low: 100, high: 5000, unit: 'per month' } }, advantages: ['Voice'], disadvantages: ['More complex'] },
      { id: 'alt-chatbot-2', name: 'Copilot', icon: '🤖', description: 'AI Copilot', specifications: { costRange: { low: 10, high: 100, unit: 'per month' } }, advantages: ['Productivity'], disadvantages: ['Less conversational'] }
    ]
  },
  
  'Voice Assistant': {
    category: 'Conversational',
    alternatives: [
      { id: 'alt-va-1', name: 'Chatbot', icon: '💬', description: 'Chatbot', specifications: { costRange: { low: 10, high: 500, unit: 'per month' } }, advantages: ['Simpler'], disadvantages: ['No voice'] },
      { id: 'alt-va-2', name: 'Copilot', icon: '🤖', description: 'Copilot', specifications: { costRange: { low: 10, high: 100, unit: 'per month' } }, advantages: ['Productivity'], disadvantages: ['Less conversational'] }
    ]
  },
  
  'Copilot': {
    category: 'Conversational',
    alternatives: [
      { id: 'alt-copilot-1', name: 'Chatbot', icon: '💬', description: 'Chatbot', specifications: { costRange: { low: 10, high: 500, unit: 'per month' } }, advantages: ['Conversational'], disadvantages: ['Less productivity'] },
      { id: 'alt-copilot-2', name: 'Agentic Tool Usage', icon: '🛠️', description: 'AI Agent', specifications: { costRange: { low: 100, high: 5000, unit: 'per month' } }, advantages: ['Autonomous'], disadvantages: ['Complex'] }
    ]
  },
  
  'Robotic Control System': {
    category: 'Autonomous',
    alternatives: [
      { id: 'alt-rcs-1', name: 'Agentic Tool Usage', icon: '🛠️', description: 'AI Agent', specifications: { costRange: { low: 100, high: 5000, unit: 'per month' } }, advantages: ['Flexible'], disadvantages: ['Less physical'] },
      { id: 'alt-rcs-2', name: 'Autonomous Vehicle Path Planner', icon: '🚗', description: 'Path planner', specifications: { costRange: { low: 1000, high: 100000, unit: 'per system' } }, advantages: ['Navigation'], disadvantages: ['Vehicle specific'] }
    ]
  },
  
  'Automated Trading Algorithm': {
    category: 'Autonomous',
    alternatives: [
      { id: 'alt-ata-1', name: 'Robotic Control System', icon: '🦾', description: 'Robotic control', specifications: { costRange: { low: 1000, high: 100000, unit: 'per system' } }, advantages: ['Physical'], disadvantages: ['Not trading'] },
      { id: 'alt-ata-2', name: 'Agentic Tool Usage', icon: '🛠️', description: 'AI Agent', specifications: { costRange: { low: 100, high: 5000, unit: 'per month' } }, advantages: ['Flexible'], disadvantages: ['Less specialized'] }
    ]
  },
  
  'Autonomous Vehicle Path Planner': {
    category: 'Autonomous',
    alternatives: [
      { id: 'alt-avpp-1', name: 'Robotic Control System', icon: '🦾', description: 'Robotic control', specifications: { costRange: { low: 1000, high: 100000, unit: 'per system' } }, advantages: ['General robotics'], disadvantages: ['Less vehicle'] },
      { id: 'alt-avpp-2', name: 'Agentic Tool Usage', icon: '🛠️', description: 'AI Agent', specifications: { costRange: { low: 100, high: 5000, unit: 'per month' } }, advantages: ['Flexible'], disadvantages: ['Less physical'] }
    ]
  },
  
  'Agentic Tool Usage': {
    category: 'Autonomous',
    alternatives: [
      { id: 'alt-atu-1', name: 'Copilot', icon: '🤖', description: 'Copilot', specifications: { costRange: { low: 10, high: 100, unit: 'per month' } }, advantages: ['Simple'], disadvantages: ['Less autonomous'] },
      { id: 'alt-atu-2', name: 'Robotic Control System', icon: '🦾', description: 'Robotic control', specifications: { costRange: { low: 1000, high: 100000, unit: 'per system' } }, advantages: ['Physical'], disadvantages: ['Less flexible'] }
    ]
  },
  
  'Recommendation Carousel': {
    category: 'Personalization',
    alternatives: [
      { id: 'alt-rc-1', name: 'A/B Testing Frame', icon: '🧪', description: 'A/B testing', specifications: { costRange: { low: 0, high: 1000, unit: 'per month' } }, advantages: ['Testing'], disadvantages: ['Less personalization'] },
      { id: 'alt-rc-2', name: 'Session Manager', icon: '🔐', description: 'Session management', specifications: { costRange: { low: 0, high: 500, unit: 'per month' } }, advantages: ['Context'], disadvantages: ['Less recommendation'] }
    ]
  },
  
  'Session Manager': {
    category: 'Personalization',
    alternatives: [
      { id: 'alt-sm-1', name: 'Redis', icon: '⚡', description: 'Redis sessions', specifications: { costRange: { low: 0, high: 500, unit: 'per month' } }, advantages: ['Fast'], disadvantages: ['Less features'] },
      { id: 'alt-sm-2', name: 'JWT Tokens', icon: '🔑', description: 'JWT-based sessions', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Stateless'], disadvantages: ['No revocation'] }
    ]
  },
  
  'A/B Testing Frame': {
    category: 'Personalization',
    alternatives: [
      { id: 'alt-ab-1', name: 'Feature Flags', icon: '🚩', description: 'Feature flags', specifications: { costRange: { low: 0, high: 500, unit: 'per month' } }, advantages: ['Control'], disadvantages: ['Less testing'] },
      { id: 'alt-ab-2', name: 'Multi-Armed Bandit', icon: '🎰', description: 'MAB testing', specifications: { costRange: { low: 0, high: 100, unit: 'per implementation' } }, advantages: ['Optimal'], disadvantages: ['Complex'] }
    ]
  },
  
  'Feature Flags': {
    category: 'Personalization',
    alternatives: [
      { id: 'alt-ff-1', name: 'A/B Testing Frame', icon: '🧪', description: 'A/B testing', specifications: { costRange: { low: 0, high: 1000, unit: 'per month' } }, advantages: ['Testing'], disadvantages: ['Less control'] },
      { id: 'alt-ff-2', name: 'Session Manager', icon: '🔐', description: 'Session management', specifications: { costRange: { low: 0, high: 500, unit: 'per month' } }, advantages: ['Context'], disadvantages: ['Less flags'] }
    ]
  },
  
  // ==================== LAYER 11: OBSERVABILITY ====================
  
  'Grafana': {
    category: 'Monitoring',
    alternatives: [
      { id: 'alt-grafana-1', name: 'Kibana', icon: '📊', description: 'Kibana', specifications: { costRange: { low: 0, high: 1000, unit: 'per month' } }, advantages: ['ELK'], disadvantages: ['Less metrics'] },
      { id: 'alt-grafana-2', name: 'Datadog Dashboards', icon: '📈', description: 'Datadog', specifications: { costRange: { low: 15, high: 5000, unit: 'per month' } }, advantages: ['Integrated'], disadvantages: ['Cost'] }
    ]
  },
  
  'Datadog': {
    category: 'Monitoring',
    alternatives: [
      { id: 'alt-datadog-1', name: 'Prometheus + Grafana', icon: '📊', description: 'Open source stack', specifications: { costRange: { low: 0, high: 1000, unit: 'per month' } }, advantages: ['Free'], disadvantages: ['Self-managed'] },
      { id: 'alt-datadog-2', name: 'New Relic', icon: '📊', description: 'New Relic', specifications: { costRange: { low: 0, high: 2000, unit: 'per month' } }, advantages: ['APM'], disadvantages: ['Cost'] }
    ]
  },
  
  'New Relic': {
    category: 'Monitoring',
    alternatives: [
      { id: 'alt-nr-1', name: 'Datadog', icon: '🐕', description: 'Datadog', specifications: { costRange: { low: 15, high: 5000, unit: 'per month' } }, advantages: ['More features'], disadvantages: ['Cost'] },
      { id: 'alt-nr-2', name: 'Prometheus + Grafana', icon: '📊', description: 'Open source', specifications: { costRange: { low: 0, high: 1000, unit: 'per month' } }, advantages: ['Free'], disadvantages: ['Self-managed'] }
    ]
  },
  
  'CloudWatch': {
    category: 'Monitoring',
    alternatives: [
      { id: 'alt-cw-1', name: 'Prometheus', icon: '📊', description: 'Prometheus', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Free'], disadvantages: ['Self-managed'] },
      { id: 'alt-cw-2', name: 'Datadog', icon: '🐕', description: 'Datadog', specifications: { costRange: { low: 15, high: 5000, unit: 'per month' } }, advantages: ['More features'], disadvantages: ['Cost'] }
    ]
  },
  
  'Evidently AI': {
    category: 'Model Monitoring',
    alternatives: [
      { id: 'alt-evidently-1', name: 'WhyLabs', icon: '🔍', description: 'WhyLabs', specifications: { costRange: { low: 100, high: 5000, unit: 'per month' } }, advantages: ['Managed'], disadvantages: ['Cost'] },
      { id: 'alt-evidently-2', name: 'Fiddler AI', icon: '🎻', description: 'Fiddler AI', specifications: { costRange: { low: 100, high: 5000, unit: 'per month' } }, advantages: ['Explainability'], disadvantages: ['Cost'] }
    ]
  },
  
  'WhyLabs': {
    category: 'Model Monitoring',
    alternatives: [
      { id: 'alt-whylabs-1', name: 'Evidently AI', icon: '📉', description: 'Evidently AI', specifications: { costRange: { low: 0, high: 500, unit: 'per month' } }, advantages: ['Free tier'], disadvantages: ['Self-hosted'] },
      { id: 'alt-whylabs-2', name: 'Fiddler AI', icon: '🎻', description: 'Fiddler AI', specifications: { costRange: { low: 100, high: 5000, unit: 'per month' } }, advantages: ['Explainability'], disadvantages: ['Cost'] }
    ]
  },
  
  'Fiddler AI': {
    category: 'Model Monitoring',
    alternatives: [
      { id: 'alt-fiddler-1', name: 'Evidently AI', icon: '📉', description: 'Evidently AI', specifications: { costRange: { low: 0, high: 500, unit: 'per month' } }, advantages: ['Free'], disadvantages: ['Less features'] },
      { id: 'alt-fiddler-2', name: 'WhyLabs', icon: '🔍', description: 'WhyLabs', specifications: { costRange: { low: 100, high: 5000, unit: 'per month' } }, advantages: ['Managed'], disadvantages: ['Cost'] }
    ]
  },
  
  'SHAP': {
    category: 'Explainability',
    alternatives: [
      { id: 'alt-shap-1', name: 'LIME', icon: '🍋', description: 'LIME', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Local'], disadvantages: ['Less stable'] },
      { id: 'alt-shap-2', name: 'Integrated Gradients', icon: '📐', description: 'Integrated Gradients', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Neural'], disadvantages: ['Slower'] }
    ]
  },
  
  'LIME': {
    category: 'Explainability',
    alternatives: [
      { id: 'alt-lime-1', name: 'SHAP', icon: '🔍', description: 'SHAP', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['More stable'], disadvantages: ['Slower'] },
      { id: 'alt-lime-2', name: 'Integrated Gradients', icon: '📐', description: 'Integrated Gradients', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Neural'], disadvantages: ['Slower'] }
    ]
  },
  
  'Integrated Gradients': {
    category: 'Explainability',
    alternatives: [
      { id: 'alt-ig-1', name: 'SHAP', icon: '🔍', description: 'SHAP', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['General'], disadvantages: ['Slower'] },
      { id: 'alt-ig-2', name: 'Attention Visualization', icon: '👁️', description: 'Attention viz', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Transformer'], disadvantages: ['Attention only'] }
    ]
  },
  
  'Attention Visualization': {
    category: 'Explainability',
    alternatives: [
      { id: 'alt-attn-1', name: 'SHAP', icon: '🔍', description: 'SHAP', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['General'], disadvantages: ['Not attention'] },
      { id: 'alt-attn-2', name: 'LIME', icon: '🍋', description: 'LIME', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Local'], disadvantages: ['Less stable'] }
    ]
  },
  
  'OpenTelemetry': {
    category: 'Tracing',
    alternatives: [
      { id: 'alt-otel-1', name: 'Jaeger', icon: '🔍', description: 'Jaeger tracing', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Mature'], disadvantages: ['Less standard'] },
      { id: 'alt-otel-2', name: 'Zipkin', icon: '🔍', description: 'Zipkin', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Simple'], disadvantages: ['Less features'] }
    ]
  },
  
  'Sentry': {
    category: 'Error Tracking',
    alternatives: [
      { id: 'alt-sentry-1', name: 'Rollbar', icon: '🐛', description: 'Rollbar', specifications: { costRange: { low: 0, high: 100, unit: 'per month' } }, advantages: ['Simple'], disadvantages: ['Less features'] },
      { id: 'alt-sentry-2', name: 'Bugsnag', icon: '🐛', description: 'Bugsnag', specifications: { costRange: { low: 0, high: 100, unit: 'per month' } }, advantages: ['Mobile'], disadvantages: ['Less web'] }
    ]
  },
  
  'Log Aggregation (ELK)': {
    category: 'Logging',
    alternatives: [
      { id: 'alt-elk-1', name: 'Loki + Grafana', icon: '📊', description: 'Loki stack', specifications: { costRange: { low: 0, high: 500, unit: 'per month' } }, advantages: ['Lighter'], disadvantages: ['Less features'] },
      { id: 'alt-elk-2', name: 'Datadog Logs', icon: '🐕', description: 'Datadog Logs', specifications: { costRange: { low: 15, high: 5000, unit: 'per month' } }, advantages: ['Managed'], disadvantages: ['Cost'] }
    ]
  },
  
  'Data Lineage Tools': {
    category: 'Tracing',
    alternatives: [
      { id: 'alt-dlt-1', name: 'OpenLineage', icon: '🔗', description: 'OpenLineage', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Open standard'], disadvantages: ['Newer'] },
      { id: 'alt-dlt-2', name: 'Apache Atlas', icon: '📊', description: 'Apache Atlas', specifications: { costRange: { low: 0, high: 1000, unit: 'per month' } }, advantages: ['Governance'], disadvantages: ['Hadoop-centric'] }
    ]
  },
  
  // ==================== LAYER 12: GOVERNANCE ====================
  
  'OAuth 2.0': {
    category: 'Authentication',
    alternatives: [
      { id: 'alt-oauth-1', name: 'Keycloak', icon: '🔑', description: 'Keycloak', specifications: { costRange: { low: 0, high: 500, unit: 'per month' } }, advantages: ['Full featured'], disadvantages: ['Self-hosted'] },
      { id: 'alt-oauth-2', name: 'Auth0', icon: '🔐', description: 'Auth0', specifications: { costRange: { low: 0, high: 1000, unit: 'per month' } }, advantages: ['Managed'], disadvantages: ['Cost'] },
      { id: 'alt-oauth-3', name: 'Clerk', icon: '🔐', description: 'Clerk', specifications: { costRange: { low: 0, high: 500, unit: 'per month' } }, advantages: ['Modern'], disadvantages: ['Newer'] }
    ]
  },
  
  'RBAC': {
    category: 'Authorization',
    alternatives: [
      { id: 'alt-rbac-1', name: 'ABAC', icon: '🔐', description: 'Attribute-based access', specifications: { costRange: { low: 0, high: 100, unit: 'per month' } }, advantages: ['More flexible'], disadvantages: ['Complex'] },
      { id: 'alt-rbac-2', name: 'OAuth 2.0 Scopes', icon: '🔑', description: 'OAuth scopes', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Simple'], disadvantages: ['Less granular'] }
    ]
  },
  
  'SSO (Okta)': {
    category: 'Authentication',
    alternatives: [
      { id: 'alt-sso-1', name: 'Keycloak', icon: '🔑', description: 'Keycloak SSO', specifications: { costRange: { low: 0, high: 500, unit: 'per month' } }, advantages: ['Free'], disadvantages: ['Self-hosted'] },
      { id: 'alt-sso-2', name: 'Azure AD', icon: '☁️', description: 'Azure Active Directory', specifications: { costRange: { low: 6, high: 50, unit: 'per user/month' } }, advantages: ['Microsoft'], disadvantages: ['Azure only'] },
      { id: 'alt-sso-3', name: 'Auth0', icon: '🔐', description: 'Auth0 SSO', specifications: { costRange: { low: 0, high: 1000, unit: 'per month' } }, advantages: ['Managed'], disadvantages: ['Cost'] }
    ]
  },
  
  'Network Segmentation': {
    category: 'Security',
    alternatives: [
      { id: 'alt-ns-1', name: 'VLAN', icon: '🌐', description: 'VLAN segmentation', specifications: { costRange: { low: 0, high: 100, unit: 'per month' } }, advantages: ['Simple'], disadvantages: ['Less isolated'] },
      { id: 'alt-ns-2', name: 'Zero Trust Network', icon: '🛡️', description: 'Zero Trust', specifications: { costRange: { low: 100, high: 5000, unit: 'per month' } }, advantages: ['Most secure'], disadvantages: ['Complex'] }
    ]
  },
  
  'Air-gapping': {
    category: 'Security',
    alternatives: [
      { id: 'alt-ag-1', name: 'Network Segmentation', icon: '🌐', description: 'Network segmentation', specifications: { costRange: { low: 100, high: 5000, unit: 'per month' } }, advantages: ['More practical'], disadvantages: ['Less secure'] },
      { id: 'alt-ag-2', name: 'Zero Trust Network', icon: '🛡️', description: 'Zero Trust', specifications: { costRange: { low: 100, high: 5000, unit: 'per month' } }, advantages: ['Modern'], disadvantages: ['Complex'] }
    ]
  },
  
  'Model Cards': {
    category: 'Documentation',
    alternatives: [
      { id: 'alt-mc-1', name: 'Data Cards', icon: '📄', description: 'Data cards', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Dataset docs'], disadvantages: ['Less model'] },
      { id: 'alt-mc-2', name: 'AI FactSheets', icon: '📋', description: 'IBM FactSheets', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Comprehensive'], disadvantages: ['Less adopted'] }
    ]
  },
  
  'Data Cards': {
    category: 'Documentation',
    alternatives: [
      { id: 'alt-dc-1', name: 'Model Cards', icon: '📋', description: 'Model cards', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Model docs'], disadvantages: ['Less data'] },
      { id: 'alt-dc-2', name: 'Datasheets for Datasets', icon: '📄', description: 'Datasheets', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Academic'], disadvantages: ['Less adopted'] }
    ]
  },
  
  'Git Version History': {
    category: 'Versioning',
    alternatives: [
      { id: 'alt-git-1', name: 'DVC', icon: '📦', description: 'Data Version Control', specifications: { costRange: { low: 0, high: 100, unit: 'per month' } }, advantages: ['Data versioning'], disadvantages: ['Git-based'] },
      { id: 'alt-git-2', name: 'MLflow Tracking', icon: '📦', description: 'MLflow tracking', specifications: { costRange: { low: 0, high: 1000, unit: 'per month' } }, advantages: ['ML tracking'], disadvantages: ['Less code'] }
    ]
  },
  
  'Audit Logs (SIEM)': {
    category: 'Audit',
    alternatives: [
      { id: 'alt-siem-1', name: 'Splunk', icon: '📊', description: 'Splunk', specifications: { costRange: { low: 500, high: 20000, unit: 'per month' } }, advantages: ['Powerful'], disadvantages: ['Very expensive'] },
      { id: 'alt-siem-2', name: 'IBM QRadar', icon: '🛡️', description: 'IBM QRadar', specifications: { costRange: { low: 500, high: 10000, unit: 'per month' } }, advantages: ['Enterprise'], disadvantages: ['Cost'] }
    ]
  },
  
  'Blockchain Timestamps': {
    category: 'Provenance',
    alternatives: [
      { id: 'alt-bc-1', name: 'Audit Logs (SIEM)', icon: '📊', description: 'SIEM audit', specifications: { costRange: { low: 100, high: 10000, unit: 'per month' } }, advantages: ['Standard'], disadvantages: ['Less immutable'] },
      { id: 'alt-bc-2', name: 'OpenTimestamps', icon: '⛓️', description: 'OpenTimestamps', specifications: { costRange: { low: 0, high: 10, unit: 'per month' } }, advantages: ['Free', 'Bitcoin'], disadvantages: ['Less features'] }
    ]
  },
  
  'Review Queue (Content Moderation)': {
    category: 'Human Review',
    alternatives: [
      { id: 'alt-rq-1', name: 'Escalation Path', icon: '📞', description: 'Escalation path', specifications: { costRange: { low: 0, high: 500, unit: 'per month' } }, advantages: ['Focused'], disadvantages: ['Less moderation'] },
      { id: 'alt-rq-2', name: 'Doctor-in-the-loop Dashboard', icon: '⚕️', description: 'Medical review', specifications: { costRange: { low: 1000, high: 10000, unit: 'per month' } }, advantages: ['Medical'], disadvantages: ['Specific'] }
    ]
  },
  
  'Doctor-in-the-loop Dashboard': {
    category: 'Human Review',
    alternatives: [
      { id: 'alt-dil-1', name: 'Review Queue (Content Moderation)', icon: '👁️', description: 'Review queue', specifications: { costRange: { low: 100, high: 5000, unit: 'per month' } }, advantages: ['General'], disadvantages: ['Less medical'] },
      { id: 'alt-dil-2', name: 'Escalation Path', icon: '📞', description: 'Escalation', specifications: { costRange: { low: 0, high: 500, unit: 'per month' } }, advantages: ['Simple'], disadvantages: ['Less structured'] }
    ]
  },
  
  'Kill Switch': {
    category: 'Safety',
    alternatives: [
      { id: 'alt-ks-1', name: 'Guardrails', icon: '🛡️', description: 'Guardrails', specifications: { costRange: { low: 100, high: 5000, unit: 'per month' } }, advantages: ['Preventive'], disadvantages: ['Less emergency'] },
      { id: 'alt-ks-2', name: 'Safety Classifiers', icon: '🛡️', description: 'Safety classifiers', specifications: { costRange: { low: 100, high: 1000, unit: 'per month' } }, advantages: ['Automatic'], disadvantages: ['Less manual'] }
    ]
  },
  
  'Escalation Path': {
    category: 'Human Review',
    alternatives: [
      { id: 'alt-ep-1', name: 'Review Queue (Content Moderation)', icon: '👁️', description: 'Review queue', specifications: { costRange: { low: 100, high: 5000, unit: 'per month' } }, advantages: ['Structured'], disadvantages: ['Less general'] },
      { id: 'alt-ep-2', name: 'Kill Switch', icon: '🛑', description: 'Kill switch', specifications: { costRange: { low: 100, high: 1000, unit: 'per system' } }, advantages: ['Emergency'], disadvantages: ['Less gradual'] }
    ]
  },
  
  'EU AI Act Checklist': {
    category: 'Compliance',
    alternatives: [
      { id: 'alt-eu-1', name: 'NIST AI RMF', icon: '🏛️', description: 'NIST Risk Management', specifications: { costRange: { low: 0, high: 1000, unit: 'per audit' } }, advantages: ['US standard'], disadvantages: ['Less EU'] },
      { id: 'alt-eu-2', name: 'ISO 42001', icon: '📋', description: 'ISO AI Management', specifications: { costRange: { low: 1000, high: 10000, unit: 'per certification' } }, advantages: ['International'], disadvantages: ['Cost'] }
    ]
  },
  
  'SOC2 Compliance': {
    category: 'Compliance',
    alternatives: [
      { id: 'alt-soc2-1', name: 'ISO 27001', icon: '📋', description: 'ISO 27001', specifications: { costRange: { low: 1000, high: 20000, unit: 'per certification' } }, advantages: ['International'], disadvantages: ['Cost'] },
      { id: 'alt-soc2-2', name: 'HIPAA Safeguards', icon: '🏥', description: 'HIPAA', specifications: { costRange: { low: 1000, high: 50000, unit: 'per implementation' } }, advantages: ['Healthcare'], disadvantages: ['Specific'] }
    ]
  },
  
  'HIPAA Safeguards': {
    category: 'Compliance',
    alternatives: [
      { id: 'alt-hipaa-1', name: 'SOC2 Compliance', icon: '📋', description: 'SOC2', specifications: { costRange: { low: 1000, high: 50000, unit: 'per audit' } }, advantages: ['General'], disadvantages: ['Less healthcare'] },
      { id: 'alt-hipaa-2', name: 'GDPR Compliance', icon: '⚖️', description: 'GDPR', specifications: { costRange: { low: 1000, high: 50000, unit: 'per implementation' } }, advantages: ['Privacy'], disadvantages: ['EU specific'] }
    ]
  },
  
  'Bias Audit Reports': {
    category: 'Fairness',
    alternatives: [
      { id: 'alt-bias-1', name: 'Fairlearn', icon: '⚖️', description: 'Microsoft Fairlearn', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Automated'], disadvantages: ['Less formal'] },
      { id: 'alt-bias-2', name: 'AI Fairness 360', icon: '⚖️', description: 'IBM AIF360', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Comprehensive'], disadvantages: ['Complex'] }
    ]
  },
  
  'FinOps Budgets': {
    category: 'Cost Management',
    alternatives: [
      { id: 'alt-finops-1', name: 'CloudHealth', icon: '💰', description: 'VMware CloudHealth', specifications: { costRange: { low: 100, high: 5000, unit: 'per month' } }, advantages: ['Multi-cloud'], disadvantages: ['Cost'] },
      { id: 'alt-finops-2', name: 'AWS Cost Explorer', icon: '☁️', description: 'AWS Cost Explorer', specifications: { costRange: { low: 0, high: 100, unit: 'per month' } }, advantages: ['Free'], disadvantages: ['AWS only'] }
    ]
  },
  
  'AI Portfolio Dashboard': {
    category: 'Strategy',
    alternatives: [
      { id: 'alt-aipd-1', name: 'Power BI', icon: '📊', description: 'Power BI dashboard', specifications: { costRange: { low: 10, high: 5000, unit: 'per month' } }, advantages: ['General'], disadvantages: ['Less AI'] },
      { id: 'alt-aipd-2', name: 'Custom Dashboard', icon: '📊', description: 'Custom dashboard', specifications: { costRange: { low: 1000, high: 50000, unit: 'per project' } }, advantages: ['Customized'], disadvantages: ['Cost'] }
    ]
  },
  
  'Cost-to-Serve Analytics': {
    category: 'Cost Management',
    alternatives: [
      { id: 'alt-cts-1', name: 'FinOps Budgets', icon: '💰', description: 'FinOps', specifications: { costRange: { low: 0, high: 500, unit: 'per month' } }, advantages: ['Budgets'], disadvantages: ['Less serving'] },
      { id: 'alt-cts-2', name: 'CloudHealth', icon: '💰', description: 'CloudHealth', specifications: { costRange: { low: 100, high: 5000, unit: 'per month' } }, advantages: ['Multi-cloud'], disadvantages: ['Cost'] }
    ]
  },
  
  'Model Lifecycle Management': {
    category: 'Strategy',
    alternatives: [
      { id: 'alt-mlm-1', name: 'MLflow Registry', icon: '📦', description: 'MLflow registry', specifications: { costRange: { low: 0, high: 1000, unit: 'per month' } }, advantages: ['Open source'], disadvantages: ['Less lifecycle'] },
      { id: 'alt-mlm-2', name: 'SageMaker Model Registry', icon: '☁️', description: 'SageMaker', specifications: { costRange: { low: 100, high: 5000, unit: 'per month' } }, advantages: ['Managed'], disadvantages: ['AWS only'] }
    ]
  },
  
  'Adversarial Training': {
    category: 'Security',
    alternatives: [
      { id: 'alt-adv-1', name: 'Prompt Injection Firewall', icon: '🔥', description: 'Prompt firewall', specifications: { costRange: { low: 100, high: 1000, unit: 'per month' } }, advantages: ['LLM specific'], disadvantages: ['Only prompts'] },
      { id: 'alt-adv-2', name: 'Guardrails', icon: '🛡️', description: 'Guardrails', specifications: { costRange: { low: 100, high: 5000, unit: 'per month' } }, advantages: ['Comprehensive'], disadvantages: ['Overhead'] }
    ]
  },
  
  'Prompt Injection Firewall': {
    category: 'Security',
    alternatives: [
      { id: 'alt-pif-1', name: 'Adversarial Training', icon: '🛡️', description: 'Adversarial training', specifications: { costRange: { low: 0, high: 1000, unit: 'per training' } }, advantages: ['Model-level'], disadvantages: ['Training needed'] },
      { id: 'alt-pif-2', name: 'Safety Classifiers', icon: '🛡️', description: 'Safety classifiers', specifications: { costRange: { low: 100, high: 1000, unit: 'per month' } }, advantages: ['General'], disadvantages: ['Can miss'] }
    ]
  },
  
  'Data Encryption (TLS/AES)': {
    category: 'Security',
    alternatives: [
      { id: 'alt-enc-1', name: 'Homomorphic Encryption', icon: '🔐', description: 'Fully homomorphic', specifications: { costRange: { low: 1000, high: 10000, unit: 'per month' } }, advantages: ['Compute on encrypted'], disadvantages: ['Very slow'] },
      { id: 'alt-enc-2', name: 'Zero-Knowledge Proofs', icon: '🔐', description: 'ZK proofs', specifications: { costRange: { low: 1000, high: 10000, unit: 'per month' } }, advantages: ['Privacy'], disadvantages: ['Complex'] }
    ]
  },
  
  'Rate Limiting': {
    category: 'Security',
    alternatives: [
      { id: 'alt-rl-1', name: 'WAF', icon: '🛡️', description: 'WAF', specifications: { costRange: { low: 100, high: 5000, unit: 'per month' } }, advantages: ['Broader'], disadvantages: ['Less granular'] },
      { id: 'alt-rl-2', name: 'API Gateway (Kong)', icon: '🚪', description: 'Kong rate limiting', specifications: { costRange: { low: 0, high: 1000, unit: 'per month' } }, advantages: ['API level'], disadvantages: ['Less WAF'] }
    ]
  },
  
  'WAF': {
    category: 'Security',
    alternatives: [
      { id: 'alt-waf-1', name: 'Rate Limiting', icon: '🚦', description: 'Rate limiting', specifications: { costRange: { low: 0, high: 100, unit: 'per month' } }, advantages: ['Simple'], disadvantages: ['Less broad'] },
      { id: 'alt-waf-2', name: 'Nginx ModSecurity', icon: '🌐', description: 'ModSecurity', specifications: { costRange: { low: 0, high: 0, unit: 'free' } }, advantages: ['Free'], disadvantages: ['Self-managed'] }
    ]
  }
};

// ============================================================
// HELPER FUNCTIONS - FIXED FOR COMPLETE COVERAGE
// ============================================================

export function getAlternativesForComponent(componentName) {
  if (!componentName) return [];
  
  // Direct match
  if (COMPONENT_ALTERNATIVES[componentName]) {
    return COMPONENT_ALTERNATIVES[componentName].alternatives || [];
  }
  
  // Fuzzy match - check if component name contains any key or vice versa
  for (const [key, value] of Object.entries(COMPONENT_ALTERNATIVES)) {
    if (componentName.includes(key) || key.includes(componentName)) {
      return value.alternatives || [];
    }
  }
  
  // Check individual words
  const componentWords = componentName.toLowerCase().split(/[\s()\/-]+/).filter(w => w.length > 2);
  for (const [key, value] of Object.entries(COMPONENT_ALTERNATIVES)) {
    const keyWords = key.toLowerCase().split(/[\s()\/-]+/).filter(w => w.length > 2);
    for (const cw of componentWords) {
      for (const kw of keyWords) {
        if (cw.includes(kw) || kw.includes(cw)) {
          return value.alternatives || [];
        }
      }
    }
  }
  
  // Fallback: return generic alternatives
  return [
    {
      id: `alt-generic-1`,
      name: 'Alternative A',
      icon: '🔄',
      description: 'Generic alternative option',
      specifications: { costRange: { low: 100, high: 1000, unit: 'per unit' } },
      advantages: ['Alternative option'],
      disadvantages: ['May not fully match']
    },
    {
      id: `alt-generic-2`,
      name: 'Alternative B',
      icon: '🔄',
      description: 'Another generic alternative',
      specifications: { costRange: { low: 50, high: 500, unit: 'per unit' } },
      advantages: ['Cost effective'],
      disadvantages: ['May not fully match']
    }
  ];
}

export function getBestAlternative(componentName, constraints = {}) {
  const alternatives = getAlternativesForComponent(componentName);
  if (alternatives.length === 0) return null;
  
  const scored = alternatives.map(alt => {
    let score = 50;
    if (constraints.budget === 'low' && alt.specifications?.costRange?.low < 100) score += 20;
    if (constraints.budget === 'high' && alt.specifications?.costRange?.high > 1000) score += 10;
    if (alt.advantages?.length >= 3) score += 10;
    return { ...alt, score };
  });
  
  return scored.sort((a, b) => b.score - a.score)[0];
}

export function getComponentAlternativesCount(componentName) {
  return getAlternativesForComponent(componentName).length;
}

export function findComponentWithAlternatives(componentName) {
  const alternatives = getAlternativesForComponent(componentName);
  if (alternatives.length === 0) return null;
  return { componentName, alternatives };
}

export function getAllComponentsWithAlternatives() {
  return Object.keys(COMPONENT_ALTERNATIVES);
}

export function getAlternativeById(componentName, alternativeId) {
  const alternatives = getAlternativesForComponent(componentName);
  return alternatives.find(alt => alt.id === alternativeId) || null;
}

export default COMPONENT_ALTERNATIVES;